import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Sparkles, Loader2, Camera, ArrowUp, ArrowRight, Mic, Trash2, Plus, X } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { QuickEntryConfirmDialog } from "./QuickEntryConfirmDialog";
import { FixInChatDialog } from "./FixInChatDialog";
import { DataInsightsDialog } from "./DataInsightsDialog";
import { EmojiExplosion } from "./EmojiExplosion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { compressImageForAnalysis } from "@/lib/imageCompression";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { getAllMetrics, getMetricsByCategory } from "@/lib/metricDefinitions";
import { useIsMobile } from "@/hooks/use-mobile";
import { recordAudio, isAudioRecordingAvailable } from "@/lib/audioRecorder";
import { CameraTipsDialog } from "@/components/CameraTipsDialog";
import { useFingerSize } from "@/hooks/useFingerSize";
import { pauseBackgroundSync, resumeBackgroundSync } from "@/providers/ThemeProvider";
import { format } from "date-fns";
import { hapticToggle } from "@/utils/haptics";
import { detectNativeApp, isIOS, isAndroid } from "@/lib/platform";

interface QuickEntryInputProps {
  userId: string;
  isPremium: boolean | undefined;
  selectedDate: Date;
  showStartHereTooltip?: boolean;
  onAIInputChange?: (hasInput: boolean) => void;
  onAILoadingChange?: (isLoading: boolean) => void;
  onTutorialTrigger?: (metricInfo?: { metricKey: string; metricName: string; isBoolean: boolean }) => void;
}



export const QuickEntryInput = ({ userId, isPremium, selectedDate, showStartHereTooltip = false, onAIInputChange, onAILoadingChange, onTutorialTrigger }: QuickEntryInputProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestedValues, setSuggestedValues] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageBase64, setImageBase64] = useState<string[]>([]);
  const [authLoading, setAuthLoading] = useState(true);
  const queryClient = useQueryClient();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const textInputRef = React.useRef<HTMLInputElement>(null);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [audioBase64, setAudioBase64] = useState<string | null>(null);
  const [audioContentType, setAudioContentType] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const audioRecorderAvailable = isAudioRecordingAvailable();
  const [showOverlayMode, setShowOverlayMode] = useState(false);
  const [fixInChatOpen, setFixInChatOpen] = useState(false);
  
  // Refs to track dialog state for async operations (state values are stale in closures)
  const dialogOpenRef = React.useRef(false);
  const fixInChatOpenRef = React.useRef(false);
  const scanResultsQueueRef = React.useRef<Array<any>>([]);
  const activeScansCountRef = React.useRef(0);
  // AbortController to cancel in-flight scan requests when user hits Cancel
  const scanAbortControllersRef = React.useRef<AbortController[]>([]);
  const [freeFixAvailable, setFreeFixAvailable] = useState(false); // Track if user has a free fix available
  const [dataInsightsOpen, setDataInsightsOpen] = useState(false);
  const [initialAnalyticalQuestion, setInitialAnalyticalQuestion] = useState("");
  const [imageMode, setImageMode] = useState<'ai' | 'progress' | null>(null);
  const [showImageModePicker, setShowImageModePicker] = useState(false);
  const [isPickerFadingOut, setIsPickerFadingOut] = useState(false);
  const [showCameraTips, setShowCameraTips] = useState(false);

  // Use finger size hook
  const { fingerSize, saveFingerSize, fingerSizeMm } = useFingerSize(userId);

  // Helper to close picker with fade animation
  const closeImageModePicker = useCallback(() => {
    if (showImageModePicker && !isPickerFadingOut) {
      setIsPickerFadingOut(true);
      setTimeout(() => {
        setShowImageModePicker(false);
        setIsPickerFadingOut(false);
      }, 200); // Match animation duration
    }
  }, [showImageModePicker, isPickerFadingOut]);

  // Auto-close image mode picker after 2.6 seconds
  useEffect(() => {
    if (showImageModePicker && !isPickerFadingOut) {
      const timer = setTimeout(() => {
        closeImageModePicker();
      }, 2625);
      return () => clearTimeout(timer);
    }
  }, [showImageModePicker, isPickerFadingOut, closeImageModePicker]);

  // State for emoji explosion animation
  const [showEmojiExplosion, setShowEmojiExplosion] = useState(false);
  const [explosionEmojis, setExplosionEmojis] = useState<string[]>([]);
  const [pendingTutorialTrigger, setPendingTutorialTrigger] = useState<(() => void) | null>(null);
  const pendingTutorialTriggerRef = React.useRef<(() => void) | null>(null);
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0);
  
  // SSE progress state for real-time AI status updates
  const [progressSteps, setProgressSteps] = useState<Array<{ stage: string; message: string; detail?: string }>>([]);

  // Progress bar state for scan animation
  const [scanProgress, setScanProgress] = useState(0);
  const scanProgressRef = React.useRef(0);
  const scanStartTimeRef = React.useRef<number | null>(null);
  const scanProgressRafRef = React.useRef<number | null>(null);
  const scanCompleteResolveRef = React.useRef<(() => void) | null>(null);

  // Queue for pending scan results (allows multiple scans while one is loading)
  const [scanResultsQueue, setScanResultsQueue] = useState<Array<{
    suggestedValues: any;
    originalText: string;
    freeFixAvailable: boolean;
  }>>([]);
  const [activeScansCount, setActiveScansCount] = useState(0);
  // Track whether user wants to add another item while scans are processing
  const [showAddAnother, setShowAddAnother] = useState(false);

  // Keep refs in sync with dialog state (refs don't go stale in async closures)
  React.useEffect(() => {
    dialogOpenRef.current = dialogOpen;
  }, [dialogOpen]);
  
  React.useEffect(() => {
    fixInChatOpenRef.current = fixInChatOpen;
  }, [fixInChatOpen]);

  React.useEffect(() => {
    scanResultsQueueRef.current = scanResultsQueue;
  }, [scanResultsQueue]);

  React.useEffect(() => {
    activeScansCountRef.current = activeScansCount;
  }, [activeScansCount]);

  React.useEffect(() => {
    pendingTutorialTriggerRef.current = pendingTutorialTrigger;
  }, [pendingTutorialTrigger]);

  // Animated placeholder examples (memoized to prevent recreation)
  const placeholderExamples = React.useMemo(() =>
    (isMobile
      ? t('quick_entry.placeholder_examples_mobile', { returnObjects: true })
      : t('quick_entry.placeholder_examples', { returnObjects: true })) as string[]
    , [isMobile, t]);

  // Loading phrases that cycle while AI processes
  const loadingPhrases = t('quick_entry.loading_phrases', { returnObjects: true }) as string[];

  // Helper function to capture frame from video
  const captureFrame = async (video: HTMLVideoElement): Promise<string> => {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');

    ctx.drawImage(video, 0, 0);

    // Compress to JPEG with high quality
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob'));
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }, 'image/jpeg', 0.92);
    });
  };

  // Helper function to open progress pic camera (single shot with back camera)
  const openProgressCamera = async () => {
    // Blur the text input to dismiss keyboard on iOS
    textInputRef.current?.blur();
    
    let stream: MediaStream | null = null;
    let overlay: HTMLDivElement | null = null;
    let currentFacingMode: 'environment' | 'user' = 'environment'; // Start with back camera for progress pics
    
    try {
      stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });
      
      // Pause background color sync and set to black for camera
      pauseBackgroundSync();
      if (typeof window !== 'undefined' && window.natively?.setAppBackgroundColor) {
        window.natively.setAppBackgroundColor('#000000');
      }
      
      // Create overlay container
      overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.width = '100%';
      overlay.style.height = '100%';
      overlay.style.zIndex = '9999';
      overlay.style.backgroundColor = 'black';

      // Add styles for scan animation
      const style = document.createElement('style');
      style.textContent = `
        @keyframes scanFlash {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .scan-line {
          position: absolute;
          left: 0;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, transparent 5%, rgba(255,255,255,0.95) 30%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.95) 70%, transparent 95%);
          box-shadow: 0 0 8px rgba(255,255,255,0.6), 0 0 20px rgba(255,255,255,0.3);
          z-index: 10000;
          opacity: 0;
          top: 0%;
          pointer-events: none;
        }
        .scan-flash {
          animation: scanFlash 200ms ease-out forwards;
        }
        @keyframes pulseDot { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .rec-dot { animation: pulseDot 1s ease-in-out infinite; }
        .pose-slider-qei { -webkit-appearance:none; appearance:none; background:rgba(255,255,255,0.3); border-radius:2px; outline:none; cursor:pointer; }
        .pose-slider-qei::-webkit-slider-thumb { -webkit-appearance:none; width:20px; height:20px; border-radius:50%; background:white; cursor:pointer; }
        .pose-slider-qei::-moz-range-thumb { width:20px; height:20px; border-radius:50%; background:white; cursor:pointer; border:none; }
        .pose-thumb-strip-qei::-webkit-scrollbar { display:none; }
      `;
      overlay.appendChild(style);

      // Create scan line element
      const scanLine = document.createElement('div');
      scanLine.className = 'scan-line';
      overlay.appendChild(scanLine);
      
      // Create video element
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;
      video.muted = true;
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'cover';
      video.style.opacity = '0';
      video.style.transition = 'opacity 0.3s ease-out';

      video.play().catch(e => console.warn('Video play failed:', e));

      // Wait for video to load before showing
      video.onloadeddata = () => {
        video.style.opacity = '1';
        video.style.transform = currentFacingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
      };

      overlay.appendChild(video);
      document.body.appendChild(overlay);

      // === POSE MATCH OVERLAY ===
      const poseOverlayImg = document.createElement('img');
      poseOverlayImg.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;opacity:0.3;display:none;pointer-events:none;z-index:10000;';
      overlay.appendChild(poseOverlayImg);

      let poseMatchActive = false;
      let poseSnaps: Array<{ id: string; image_url: string; snap_date: string }> = [];
      let currentPoseOpacity = 0.3;

      // Pose match panel (thumbnails + opacity slider)
      const posePanel = document.createElement('div');
      posePanel.style.cssText = 'position:absolute;top:80px;left:0;right:0;display:none;flex-direction:column;align-items:center;gap:8px;z-index:10002;padding:10px 12px;';

      const thumbStrip = document.createElement('div');
      thumbStrip.className = 'pose-thumb-strip-qei';
      thumbStrip.style.cssText = 'display:flex;gap:8px;overflow-x:auto;max-width:100%;padding:4px;-webkit-overflow-scrolling:touch;scrollbar-width:none;';
      posePanel.appendChild(thumbStrip);

      const sliderRow = document.createElement('div');
      sliderRow.style.cssText = 'display:flex;align-items:center;gap:8px;width:90%;max-width:280px;';

      const eyeOffIcon = document.createElement('span');
      eyeOffIcon.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';

      const opacitySlider = document.createElement('input');
      opacitySlider.type = 'range';
      opacitySlider.min = '5';
      opacitySlider.max = '80';
      opacitySlider.step = '5';
      opacitySlider.value = '30';
      opacitySlider.className = 'pose-slider-qei';
      opacitySlider.style.cssText = 'flex:1;height:4px;';

      const eyeOnIcon = document.createElement('span');
      eyeOnIcon.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';

      opacitySlider.oninput = () => {
        currentPoseOpacity = parseInt(opacitySlider.value) / 100;
        poseOverlayImg.style.opacity = String(currentPoseOpacity);
      };

      sliderRow.appendChild(eyeOffIcon);
      sliderRow.appendChild(opacitySlider);
      sliderRow.appendChild(eyeOnIcon);
      posePanel.appendChild(sliderRow);
      overlay.appendChild(posePanel);

      // Pose match toggle button (top-left)
      const poseMatchBtn = document.createElement('button');
      poseMatchBtn.innerHTML = '<div style="width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,0.15);display:flex;align-items:center;justify-content:center;transition:background 0.2s;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></div>';
      poseMatchBtn.style.cssText = 'position:absolute;top:20px;left:20px;border:none;background:transparent;cursor:pointer;padding:0;z-index:10002;';

      const populatePoseThumbs = () => {
        thumbStrip.innerHTML = '';
        poseSnaps.forEach((snap, idx) => {
          const thumb = document.createElement('img');
          thumb.src = snap.image_url;
          thumb.style.cssText = 'width:48px;height:48px;border-radius:8px;object-fit:cover;cursor:pointer;flex-shrink:0;transition:border-color 0.2s;border:2px solid ' + (idx === 0 ? 'white' : 'transparent') + ';';
          thumb.onclick = () => {
            poseOverlayImg.src = snap.image_url;
            thumbStrip.querySelectorAll('img').forEach((t, i) => {
              (t as HTMLElement).style.borderColor = i === idx ? 'white' : 'transparent';
            });
          };
          thumbStrip.appendChild(thumb);
        });
      };

      poseMatchBtn.onclick = async () => {
        if (poseMatchActive) {
          poseMatchActive = false;
          poseOverlayImg.style.display = 'none';
          posePanel.style.display = 'none';
          (poseMatchBtn.firstElementChild as HTMLElement).style.background = 'rgba(255,255,255,0.15)';
          return;
        }

        if (poseSnaps.length === 0) {
          const { data: snapData } = await supabase.from('progress_snaps').select('id, image_url, snap_date').eq('user_id', userId).not('image_url', 'is', null).order('snap_date', { ascending: false }).limit(20);
          if (!snapData || snapData.length === 0) {
            poseMatchBtn.style.transform = 'scale(1.2)';
            setTimeout(() => { poseMatchBtn.style.transform = 'scale(1)'; }, 200);
            return;
          }
          poseSnaps = snapData;
        }

        poseMatchActive = true;
        poseOverlayImg.src = poseSnaps[0].image_url;
        poseOverlayImg.style.opacity = String(currentPoseOpacity);
        poseOverlayImg.style.display = 'block';
        poseOverlayImg.style.transform = currentFacingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
        posePanel.style.display = 'flex';
        (poseMatchBtn.firstElementChild as HTMLElement).style.background = 'rgba(255,255,255,0.35)';
        populatePoseThumbs();
      };

      overlay.appendChild(poseMatchBtn);

      // Create UI container
      const uiContainer = document.createElement('div');
      uiContainer.style.position = 'absolute';
      uiContainer.style.bottom = '40px';
      uiContainer.style.left = '50%';
      uiContainer.style.transform = 'translateX(-50%)';
      uiContainer.style.display = 'flex';
      uiContainer.style.flexDirection = 'column';
      uiContainer.style.alignItems = 'center';
      uiContainer.style.gap = '16px';
      uiContainer.style.zIndex = '10001';

      // --- Hold-to-record button ---
      const MAX_RECORD_SECONDS = 10;
      const HOLD_THRESHOLD = 300; // ms before we start recording
      let holdTimer: ReturnType<typeof setTimeout> | null = null;
      let mediaRecorder: MediaRecorder | null = null;
      let recordedChunks: Blob[] = [];
      let isRecording = false;
      let recordStartTime = 0;
      let recordTimerInterval: ReturnType<typeof setInterval> | null = null;
      let autoStopTimer: ReturnType<typeof setTimeout> | null = null;

      // Button container (holds SVG ring + inner circle)
      const takePicBtn = document.createElement('button');
      takePicBtn.style.border = 'none';
      takePicBtn.style.background = 'transparent';
      takePicBtn.style.cursor = 'pointer';
      takePicBtn.style.padding = '0';
      takePicBtn.style.transition = 'transform 150ms ease-out';
      takePicBtn.style.position = 'relative';
      takePicBtn.style.width = '84px';
      takePicBtn.style.height = '84px';
      takePicBtn.style.touchAction = 'none'; // prevent scroll on hold

      // SVG progress ring
      const ringSize = 84;
      const ringStroke = 4;
      const ringRadius = (ringSize - ringStroke) / 2;
      const ringCircumference = 2 * Math.PI * ringRadius;
      const ringSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      ringSvg.setAttribute('width', String(ringSize));
      ringSvg.setAttribute('height', String(ringSize));
      ringSvg.style.position = 'absolute';
      ringSvg.style.top = '0';
      ringSvg.style.left = '0';
      ringSvg.style.transform = 'rotate(-90deg)';
      // Background circle
      const bgCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      bgCircle.setAttribute('cx', String(ringSize / 2));
      bgCircle.setAttribute('cy', String(ringSize / 2));
      bgCircle.setAttribute('r', String(ringRadius));
      bgCircle.setAttribute('fill', 'none');
      bgCircle.setAttribute('stroke', 'rgba(255,255,255,0.25)');
      bgCircle.setAttribute('stroke-width', String(ringStroke));
      ringSvg.appendChild(bgCircle);
      // Progress circle
      const progressCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      progressCircle.setAttribute('cx', String(ringSize / 2));
      progressCircle.setAttribute('cy', String(ringSize / 2));
      progressCircle.setAttribute('r', String(ringRadius));
      progressCircle.setAttribute('fill', 'none');
      progressCircle.setAttribute('stroke', '#ef4444');
      progressCircle.setAttribute('stroke-width', String(ringStroke));
      progressCircle.setAttribute('stroke-linecap', 'round');
      progressCircle.setAttribute('stroke-dasharray', String(ringCircumference));
      progressCircle.setAttribute('stroke-dashoffset', String(ringCircumference));
      progressCircle.style.transition = 'stroke-dashoffset 0.25s linear';
      ringSvg.appendChild(progressCircle);
      ringSvg.style.opacity = '0';
      takePicBtn.appendChild(ringSvg);

      // Outer circle (white border)
      const outerCircle = document.createElement('div');
      outerCircle.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:72px;height:72px;border-radius:50%;background:rgba(255,255,255,0.2);display:flex;align-items:center;justify-content:center;border:4px solid white;transition:border-color 0.2s;';
      // Inner circle (white, becomes red square when recording)
      const innerCircle = document.createElement('div');
      innerCircle.style.cssText = 'width:50px;height:50px;border-radius:50%;background:white;transition:all 0.2s ease;';
      outerCircle.appendChild(innerCircle);
      takePicBtn.appendChild(outerCircle);

      // Timer display (hidden until recording)
      const timerDisplay = document.createElement('div');
      timerDisplay.style.cssText = 'position:absolute;top:-28px;left:50%;transform:translateX(-50%);color:white;font-size:14px;font-weight:600;display:none;align-items:center;gap:6px;white-space:nowrap;';
      const recDot = document.createElement('span');
      recDot.className = 'rec-dot';
      recDot.style.cssText = 'width:8px;height:8px;border-radius:50%;background:#ef4444;display:inline-block;';
      const timerText = document.createElement('span');
      timerText.textContent = '0:00';
      timerDisplay.appendChild(recDot);
      timerDisplay.appendChild(timerText);
      takePicBtn.appendChild(timerDisplay);

      // Hint label
      const hintLabel = document.createElement('div');
      hintLabel.textContent = 'Hold to record';
      hintLabel.style.cssText = 'color:rgba(255,255,255,0.6);font-size:11px;text-align:center;pointer-events:none;margin-top:4px;';

      // Detect supported MIME type for MediaRecorder
      const getRecordMime = (): string => {
        const types = ['video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
        for (const t of types) { if (MediaRecorder.isTypeSupported(t)) return t; }
        return '';
      };

      const startRecording = () => {
        const mimeType = getRecordMime();
        if (!mimeType || !stream) return;
        isRecording = true;
        recordedChunks = [];
        try {
          mediaRecorder = new MediaRecorder(stream, { mimeType });
        } catch {
          mediaRecorder = new MediaRecorder(stream);
        }
        mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) recordedChunks.push(e.data); };
        mediaRecorder.onstop = () => {
          if (recordedChunks.length === 0) return;
          const blob = new Blob(recordedChunks, { type: mediaRecorder?.mimeType || mimeType });
          const blobUrl = URL.createObjectURL(blob);
          // Store blob on window for CreateSnap to pick up
          (window as any).__pendingSnapVideoBlob = blob;
          cleanup();
          navigate('/create-snap', {
            state: {
              selectedDate,
              initialVideoUrl: blobUrl,
              initialText: text
            }
          });
        };
        mediaRecorder.start(100);
        recordStartTime = Date.now();

        // Visuals: show ring, morph inner to red square, show timer
        ringSvg.style.opacity = '1';
        outerCircle.style.borderColor = '#ef4444';
        innerCircle.style.width = '24px';
        innerCircle.style.height = '24px';
        innerCircle.style.borderRadius = '6px';
        innerCircle.style.background = '#ef4444';
        timerDisplay.style.display = 'flex';
        hintLabel.style.display = 'none';

        recordTimerInterval = setInterval(() => {
          const elapsed = (Date.now() - recordStartTime) / 1000;
          const secs = Math.floor(elapsed);
          const frac = String(Math.floor((elapsed - secs) * 100)).padStart(2, '0');
          timerText.textContent = `${secs}:${frac}`;
          // Update progress ring
          const progress = Math.min(elapsed / MAX_RECORD_SECONDS, 1);
          progressCircle.setAttribute('stroke-dashoffset', String(ringCircumference * (1 - progress)));
        }, 50);

        autoStopTimer = setTimeout(() => stopRecording(), MAX_RECORD_SECONDS * 1000);
      };

      const stopRecording = () => {
        if (autoStopTimer) { clearTimeout(autoStopTimer); autoStopTimer = null; }
        if (recordTimerInterval) { clearInterval(recordTimerInterval); recordTimerInterval = null; }
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
        isRecording = false;
      };

      const takePhoto = async () => {
        takePicBtn.style.pointerEvents = 'none';
        takePicBtn.style.opacity = '0.5';
        try {
          await new Promise(resolve => {
            if (video.readyState >= 2) resolve(null);
            else video.onloadeddata = () => resolve(null);
          });
          scanLine.classList.remove('scan-flash');
          void scanLine.offsetWidth;
          scanLine.classList.add('scan-flash');
          overlay.style.backgroundColor = 'rgba(255,255,255,0.3)';
          setTimeout(() => overlay.style.backgroundColor = 'black', 100);
          const frame = await captureFrame(video);
          cleanup();
          navigate('/create-snap', {
            state: { selectedDate, initialImage: frame, initialText: text }
          });
        } catch (error) {
          toast.error(t('notifications.toast.failed_to_capture_photo') + ' ' + (error instanceof Error ? error.message : 'Unknown error'));
          takePicBtn.style.pointerEvents = 'auto';
          takePicBtn.style.opacity = '1';
        }
      };

      // Pointer events for tap vs hold
      takePicBtn.onpointerdown = (e) => {
        e.preventDefault();
        holdTimer = setTimeout(() => {
          holdTimer = null;
          startRecording();
        }, HOLD_THRESHOLD);
      };
      takePicBtn.onpointerup = (e) => {
        e.preventDefault();
        if (holdTimer) {
          // Released before threshold → take photo
          clearTimeout(holdTimer);
          holdTimer = null;
          takePhoto();
        } else if (isRecording) {
          stopRecording();
        }
      };
      takePicBtn.onpointercancel = () => {
        if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
        if (isRecording) stopRecording();
      };
      takePicBtn.onpointerleave = () => {
        // Don't cancel on leave — user may slide finger slightly
      };

      // Close button
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = '✕';
      closeBtn.style.position = 'absolute';
      closeBtn.style.top = '20px';
      closeBtn.style.right = '20px';
      closeBtn.style.fontSize = '32px';
      closeBtn.style.color = 'white';
      closeBtn.style.background = 'rgba(0,0,0,0.5)';
      closeBtn.style.border = 'none';
      closeBtn.style.borderRadius = '50%';
      closeBtn.style.width = '50px';
      closeBtn.style.height = '50px';
      closeBtn.style.cursor = 'pointer';
      closeBtn.style.zIndex = '10002';

      // Flip camera button
      const flipCameraBtn = document.createElement('button');
      flipCameraBtn.innerHTML = '<div style="width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5"/><path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5"/><circle cx="12" cy="12" r="3"/><path d="m18 22-3-3 3-3"/><path d="m6 2 3 3-3 3"/></svg></div>';
      flipCameraBtn.style.position = 'absolute';
      flipCameraBtn.style.bottom = '20px';
      flipCameraBtn.style.right = '20px';
      flipCameraBtn.style.border = 'none';
      flipCameraBtn.style.background = 'transparent';
      flipCameraBtn.style.cursor = 'pointer';
      flipCameraBtn.style.padding = '0';
      flipCameraBtn.style.zIndex = '10002';

      flipCameraBtn.onclick = async () => {
        try {
          stream.getTracks().forEach(track => track.stop());
          currentFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
          
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: currentFacingMode },
              width: { ideal: 1920 },
              height: { ideal: 1080 }
            }
          });
          
          video.srcObject = stream;
          await video.play();
          
          // Mirror video when using front camera
          video.style.transform = currentFacingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
          if (poseMatchActive) {
            poseOverlayImg.style.transform = currentFacingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
          }
        } catch (error) {
          console.error('Failed to flip camera:', error);
          toast.error(t('notifications.toast.failed_to_flip_camera'));
        }
      };

      // Upload button (bottom left)
      const uploadBtn = document.createElement('button');
      uploadBtn.innerHTML = '<div style="width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg></div>';
      uploadBtn.style.position = 'absolute';
      uploadBtn.style.bottom = '20px';
      uploadBtn.style.left = '20px';
      uploadBtn.style.border = 'none';
      uploadBtn.style.background = 'transparent';
      uploadBtn.style.cursor = 'pointer';
      uploadBtn.style.padding = '0';
      uploadBtn.style.zIndex = '10002';

      // Hidden file input for upload
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*,video/*';
      fileInput.style.display = 'none';

      fileInput.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            if (file.type.startsWith('video/')) {
              // Validate duration ≤ 10s
              const url = URL.createObjectURL(file);
              const tempVid = document.createElement('video');
              tempVid.preload = 'metadata';
              tempVid.src = url;
              await new Promise<void>((res, rej) => {
                tempVid.onloadedmetadata = () => res();
                tempVid.onerror = () => rej(new Error('Cannot read video'));
              });
              if (tempVid.duration > 10.5) {
                URL.revokeObjectURL(url);
                toast.error(t('notifications.toast.video_must_be_10_seconds_or_shorter'));
                return;
              }
              // Store blob for CreateSnap
              (window as any).__pendingSnapVideoBlob = file;
              cleanup();
              navigate('/create-snap', {
                state: { selectedDate, initialVideoUrl: url, initialText: text }
              });
            } else {
              const reader = new FileReader();
              reader.onload = (event) => {
                const base64 = event.target?.result as string;
                cleanup();
                navigate('/create-snap', {
                  state: { selectedDate, initialImage: base64, initialText: text }
                });
              };
              reader.readAsDataURL(file);
            }
          } catch (error) {
            toast.error(t('notifications.toast.failed_to_load_image'));
          }
        }
      };

      uploadBtn.onclick = () => fileInput.click();
      
      const cleanup = () => {
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(overlay);
        // Resume background color sync
        resumeBackgroundSync();
      };
      
      closeBtn.onclick = cleanup;

      uiContainer.appendChild(takePicBtn);
      uiContainer.appendChild(hintLabel);
      overlay.appendChild(uiContainer);
      overlay.appendChild(closeBtn);
      overlay.appendChild(flipCameraBtn);
      overlay.appendChild(uploadBtn);
      overlay.appendChild(fileInput);

    } catch (error) {
      toast.error(t('notifications.toast.failed_to_access_camera') + ' ' + (error instanceof Error ? error.message : 'Unknown error'));
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (overlay && overlay.parentNode) {
        document.body.removeChild(overlay);
      }
      // Resume background color sync
      resumeBackgroundSync();
    }
  };

  // Helper function to open video camera with UI
  const openVideoCamera = async () => {
    // Blur the text input to dismiss keyboard on iOS
    textInputRef.current?.blur();
    
    // Check if we can add more images
    const currentImageCount = imageBase64.length;
    const maxImages = 3;
    const neededImages = maxImages - currentImageCount;

    if (neededImages <= 0) {
      toast.error(t('quick_entry.camera.max_images_error'));
      return;
    }

    let stream: MediaStream | null = null;
    let overlayAdded = false;
    let overlay: HTMLDivElement | null = null;
    let currentFacingMode: 'environment' | 'user' = 'environment';

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      // Pause background color sync and set to black for camera
      pauseBackgroundSync();
      if (typeof window !== 'undefined' && window.natively?.setAppBackgroundColor) {
        window.natively.setAppBackgroundColor('#000000');
      }

      // Create overlay container
      overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.width = '100%';
      overlay.style.height = '100%';
      overlay.style.zIndex = '9999';
      overlay.style.backgroundColor = 'black';

      // Create loading indicator
      const loadingContainer = document.createElement('div');
      loadingContainer.style.position = 'absolute';
      loadingContainer.style.top = '50%';
      loadingContainer.style.left = '50%';
      loadingContainer.style.transform = 'translate(-50%, -50%)';
      loadingContainer.style.display = 'flex';
      loadingContainer.style.flexDirection = 'column';
      loadingContainer.style.alignItems = 'center';
      loadingContainer.style.gap = '16px';
      loadingContainer.style.zIndex = '10002';
      loadingContainer.style.transition = 'opacity 0.3s ease-out';

      // Spinning scanner icon
      const spinner = document.createElement('div');
      spinner.style.width = '48px';
      spinner.style.height = '48px';
      spinner.style.border = '3px solid rgba(255,255,255,0.2)';
      spinner.style.borderTopColor = 'rgba(255,255,255,0.9)';
      spinner.style.borderRadius = '50%';
      spinner.style.animation = 'spin 0.8s linear infinite';

      const loadingText = document.createElement('div');
      loadingText.textContent = t('quick_entry.camera.loading_scanner');
      loadingText.style.color = 'rgba(255,255,255,0.8)';
      loadingText.style.fontSize = '14px';
      loadingText.style.fontWeight = '500';
      loadingText.style.letterSpacing = '0.5px';

      loadingContainer.appendChild(spinner);
      loadingContainer.appendChild(loadingText);
      overlay.appendChild(loadingContainer);

      // Add styles for scan animation
      const style = document.createElement('style');
      style.textContent = `
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes scanFlash {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .scan-line {
          position: absolute;
          left: 0;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, transparent 5%, rgba(255,255,255,0.95) 30%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.95) 70%, transparent 95%);
          box-shadow: 0 0 8px rgba(255,255,255,0.6), 0 0 20px rgba(255,255,255,0.3);
          z-index: 10000;
          opacity: 0;
          top: 0%;
          pointer-events: none;
        }
        .scan-flash {
          animation: scanFlash 200ms ease-out forwards;
        }
        .progress-marker {
          position: absolute;
          top: -4px;
          height: calc(100% + 8px);
          width: 2px;
          background: rgba(255,255,255,0.4);
          border-radius: 1px;
          transition: background 150ms ease-out;
        }
        .progress-marker::after {
          content: '';
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-top: 5px solid rgba(255,255,255,0.4);
          transition: border-top-color 150ms ease-out;
        }
        .progress-marker.captured {
          background: rgba(255,255,255,0.9);
        }
        .progress-marker.captured::after {
          border-top-color: rgba(255,255,255,0.9);
        }
      `;
      overlay.appendChild(style);

      // Create scan line element
      const scanLine = document.createElement('div');
      scanLine.className = 'scan-line';
      overlay.appendChild(scanLine);

      // Create video element
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;
      video.muted = true; // Required for autoplay on mobile
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'cover';
      // Start hidden to prevent zoom flash during load
      video.style.opacity = '0';
      video.style.transition = 'opacity 0.3s ease-out';

      // Explicitly play video (needed for some mobile browsers)
      video.play().catch(e => console.warn('Video play failed:', e));

      // Create four-corners overlay (targeting/framing guide)
      const cornerOverlay = document.createElement('div');
      cornerOverlay.style.position = 'absolute';
      cornerOverlay.style.top = '50%';
      cornerOverlay.style.left = '50%';
      cornerOverlay.style.transform = 'translate(-50%, -50%)';
      // Use min of viewport width/height to ensure square shape
      cornerOverlay.style.width = 'min(70vw, 70vh, 400px)';
      cornerOverlay.style.height = 'min(70vw, 70vh, 400px)';
      cornerOverlay.style.pointerEvents = 'none';
      cornerOverlay.style.zIndex = '10001';

      // Add corner brackets
      const cornerSize = '24px';
      const cornerThickness = '3px';
      const corners = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
      
      corners.forEach(corner => {
        const bracket = document.createElement('div');
        bracket.style.position = 'absolute';
        bracket.style.width = cornerSize;
        bracket.style.height = cornerSize;
        
        // Position corners
        if (corner.includes('top')) bracket.style.top = '0';
        else bracket.style.bottom = '0';
        if (corner.includes('left')) bracket.style.left = '0';
        else bracket.style.right = '0';
        
        // Create L-shaped bracket with pseudo-elements via borders
        bracket.style.borderStyle = 'solid';
        bracket.style.borderColor = 'white';
        bracket.style.borderWidth = '0';
        
        if (corner === 'top-left') {
          bracket.style.borderTopWidth = cornerThickness;
          bracket.style.borderLeftWidth = cornerThickness;
        } else if (corner === 'top-right') {
          bracket.style.borderTopWidth = cornerThickness;
          bracket.style.borderRightWidth = cornerThickness;
        } else if (corner === 'bottom-left') {
          bracket.style.borderBottomWidth = cornerThickness;
          bracket.style.borderLeftWidth = cornerThickness;
        } else if (corner === 'bottom-right') {
          bracket.style.borderBottomWidth = cornerThickness;
          bracket.style.borderRightWidth = cornerThickness;
        }
        
        cornerOverlay.appendChild(bracket);
      });

      // Fade in video once stream is actually playing and stable
      const fadeInVideo = () => {
        // Wait a frame to ensure layout is complete
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            video.style.opacity = '1';
            // Hide loading indicator
            loadingContainer.style.opacity = '0';
            // Remove loading container after fade
            setTimeout(() => {
              if (loadingContainer.parentNode) {
                loadingContainer.parentNode.removeChild(loadingContainer);
              }
            }, 300);
          });
        });
      };
      video.addEventListener('canplay', fadeInVideo, { once: true });

      // Create UI container
      const uiContainer = document.createElement('div');
      uiContainer.style.position = 'absolute';
      uiContainer.style.bottom = '0';
      uiContainer.style.left = '0';
      uiContainer.style.right = '0';
      uiContainer.style.padding = '24px';
      uiContainer.style.display = 'flex';
      uiContainer.style.flexDirection = 'column';
      uiContainer.style.alignItems = 'center';
      uiContainer.style.gap = '16px';
      uiContainer.style.background = 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)';

      // Progress bar container (always visible)
      const progressContainer = document.createElement('div');
      progressContainer.style.width = '80%';
      progressContainer.style.maxWidth = '300px';
      progressContainer.style.height = '4px';
      progressContainer.style.backgroundColor = 'rgba(255,255,255,0.15)';
      progressContainer.style.borderRadius = '2px';
      progressContainer.style.overflow = 'visible';
      progressContainer.style.position = 'relative';
      progressContainer.style.marginBottom = '8px';
      // Fade edges to 0 opacity (left 4% and right 4%)
      progressContainer.style.maskImage = 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)';
      progressContainer.style.webkitMaskImage = 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)';

      // Progress bar fill
      const progressFill = document.createElement('div');
      progressFill.style.height = '100%';
      progressFill.style.width = '0%';
      progressFill.style.backgroundColor = 'rgba(255,255,255,0.9)';
      progressFill.style.borderRadius = '2px';
      progressFill.style.boxShadow = '0 0 8px rgba(255,255,255,0.4)';
      // No transition - we update at 60fps so CSS transitions cause jitter
      progressContainer.appendChild(progressFill);

      // Calculate capture times and total duration upfront (1.3x faster = 75% of original duration)
      const captureInterval = 2550; // 3400 * 0.75 = 2550ms between captures
      const firstCapture = 1913; // 2550 * 0.75 = 1912.5ms before first capture
      const captureTimes = Array.from({ length: neededImages }, (_, i) => (i * captureInterval) + firstCapture);
      const totalDuration = captureTimes[captureTimes.length - 1] + 1275; // Extra 1275ms buffer at end (1700 * 0.75)

      // Create progress markers for capture points (visible before recording)
      const progressMarkers: HTMLElement[] = [];
      captureTimes.forEach((time, i) => {
        const marker = document.createElement('div');
        marker.className = 'progress-marker';
        marker.style.left = `${(time / totalDuration) * 100}%`;
        progressContainer.appendChild(marker);
        progressMarkers.push(marker);
      });

      // Frame capture indicators
      const indicatorsContainer = document.createElement('div');
      indicatorsContainer.style.display = 'flex';
      indicatorsContainer.style.gap = '12px';
      indicatorsContainer.style.marginBottom = '8px';
      indicatorsContainer.style.opacity = '0';
      indicatorsContainer.style.transition = 'opacity 300ms';

      // Dynamic labels based on needed images
      const allLabels = [t('quick_entry.camera.side_1'), t('quick_entry.camera.side_2'), t('quick_entry.camera.top')];
      const frameLabels = allLabels.slice(currentImageCount);
      const indicators: HTMLElement[] = [];

      // Dynamic instruction text based on what's already captured
      const getInstructionText = () => {
        if (currentImageCount === 0) {
          return t('quick_entry.camera.instruction_side_1');
        } else if (currentImageCount === 1) {
          return t('quick_entry.camera.instruction_side_2');
        } else {
          return t('quick_entry.camera.instruction_top');
        }
      };

      frameLabels.forEach((label, i) => {
        const indicator = document.createElement('div');
        indicator.style.display = 'flex';
        indicator.style.flexDirection = 'column';
        indicator.style.alignItems = 'center';
        indicator.style.gap = '4px';

        const circle = document.createElement('div');
        circle.style.width = '28px';
        circle.style.height = '28px';
        circle.style.borderRadius = '50%';
        circle.style.border = '1.5px solid rgba(255,255,255,0.35)';
        circle.style.backgroundColor = 'rgba(255,255,255,0.05)';
        circle.style.backdropFilter = 'blur(4px)';
        circle.style.transition = 'all 250ms ease-out';

        const text = document.createElement('div');
        text.textContent = label;
        text.style.fontSize = '11px';
        text.style.color = 'rgba(255,255,255,0.7)';
        text.style.transition = 'color 300ms';

        indicator.appendChild(circle);
        indicator.appendChild(text);
        indicatorsContainer.appendChild(indicator);
        indicators.push(indicator);
      });

      // Instructions text
      const instructions = document.createElement('div');
      instructions.textContent = getInstructionText();
      instructions.style.color = 'white';
      instructions.style.fontSize = '14px';
      instructions.style.textAlign = 'center';
      instructions.style.marginBottom = '8px';

      // Buttons container for record and quick snap
      const buttonsContainer = document.createElement('div');
      buttonsContainer.style.display = 'flex';
      buttonsContainer.style.alignItems = 'center';
      buttonsContainer.style.gap = '24px';

      // Record button
      const recordBtn = document.createElement('button');
      recordBtn.innerHTML = '<div style="width: 72px; height: 72px; border-radius: 50%; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; border: 4px solid white;"><div style="width: 50px; height: 50px; border-radius: 50%; background: red;"></div></div>';
      recordBtn.style.border = 'none';
      recordBtn.style.background = 'transparent';
      recordBtn.style.cursor = 'pointer';
      recordBtn.style.padding = '0';

      // Quick snap button (single photo)
      const quickSnapBtn = document.createElement('button');
      quickSnapBtn.innerHTML = '<div style="width: 48px; height: 48px; border-radius: 50%; background: rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center; border: 3px solid rgba(255,255,255,0.8);"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3.5"/><path d="M3 9a2 2 0 0 1 2-2h.93a2 2 0 0 0 1.664-.89l.812-1.22A2 2 0 0 1 10.07 4h3.86a2 2 0 0 1 1.664.89l.812 1.22A2 2 0 0 0 18.07 7H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z"/></svg></div>';
      quickSnapBtn.style.border = 'none';
      quickSnapBtn.style.background = 'transparent';
      quickSnapBtn.style.cursor = 'pointer';
      quickSnapBtn.style.padding = '0';
      quickSnapBtn.style.transition = 'transform 150ms ease-out';
      quickSnapBtn.title = t('quick_entry.camera.quick_snap');

      // Quick snap click handler
      quickSnapBtn.onclick = async () => {
        quickSnapBtn.disabled = true;
        quickSnapBtn.style.opacity = '0.5';
        quickSnapBtn.style.transform = 'scale(0.95)';

        try {
          // Wait for video to be ready
          await new Promise(resolve => {
            if (video.readyState >= 2) resolve(null);
            else video.onloadeddata = () => resolve(null);
          });

          // Trigger scan flash
          scanLine.classList.remove('scan-flash');
          void scanLine.offsetWidth; // Force reflow
          scanLine.classList.add('scan-flash');

          // Screen flash effect
          overlay.style.backgroundColor = 'rgba(255,255,255,0.3)';
          setTimeout(() => overlay.style.backgroundColor = 'black', 100);

          // Capture single frame
          const frame = await captureFrame(video);

          // Add captured frame to state
          setImageBase64(prev => {
            const combined = [...prev, frame];
            return combined.slice(0, 3);
          });

          cleanup();
        } catch (error) {
          toast.error(t('quick_entry.camera.capture_error', { error: error instanceof Error ? error.message : 'Unknown error' }));
          quickSnapBtn.disabled = false;
          quickSnapBtn.style.opacity = '1';
          quickSnapBtn.style.transform = 'scale(1)';
        }
      };

      // Close button
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = '✕';
      closeBtn.style.position = 'absolute';
      closeBtn.style.top = '20px';
      closeBtn.style.right = '20px';
      closeBtn.style.fontSize = '32px';
      closeBtn.style.color = 'white';
      closeBtn.style.background = 'rgba(0,0,0,0.5)';
      closeBtn.style.border = 'none';
      closeBtn.style.borderRadius = '50%';
      closeBtn.style.width = '50px';
      closeBtn.style.height = '50px';
      closeBtn.style.cursor = 'pointer';

      // Flip camera button
      const flipCameraBtn = document.createElement('button');
      flipCameraBtn.innerHTML = '<div style="width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5"/><path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5"/><circle cx="12" cy="12" r="3"/><path d="m18 22-3-3 3-3"/><path d="m6 2 3 3-3 3"/></svg></div>';
      flipCameraBtn.style.position = 'absolute';
      flipCameraBtn.style.bottom = '20px';
      flipCameraBtn.style.right = '20px';
      flipCameraBtn.style.border = 'none';
      flipCameraBtn.style.background = 'transparent';
      flipCameraBtn.style.cursor = 'pointer';
      flipCameraBtn.style.padding = '0';
      flipCameraBtn.style.transition = 'transform 150ms ease-out';
      flipCameraBtn.title = t('quick_entry.camera.flip_camera');

      flipCameraBtn.onclick = async () => {
        try {
          // Stop current stream
          stream.getTracks().forEach(track => track.stop());

          // Toggle facing mode
          currentFacingMode = currentFacingMode === 'environment' ? 'user' : 'environment';

          // Get new stream with opposite facing mode
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: currentFacingMode },
              width: { ideal: 1920 },
              height: { ideal: 1080 }
            }
          });

          // Update video source
          video.srcObject = stream;
          await video.play();

          // Mirror video when using front camera
          video.style.transform = currentFacingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
        } catch (error) {
          console.error('Failed to flip camera:', error);
          toast.error(t('quick_entry.camera.flip_error', { defaultValue: 'Failed to flip camera' }));
        }
      };

      // Upload button (bottom left)
      const uploadBtn = document.createElement('button');
      uploadBtn.innerHTML = '<div style="width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg></div>';
      uploadBtn.style.position = 'absolute';
      uploadBtn.style.bottom = '20px';
      uploadBtn.style.left = '20px';
      uploadBtn.style.border = 'none';
      uploadBtn.style.background = 'transparent';
      uploadBtn.style.cursor = 'pointer';
      uploadBtn.style.padding = '0';
      uploadBtn.style.zIndex = '10002';

      // Hidden file input for upload
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.style.display = 'none';

      fileInput.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const reader = new FileReader();
            reader.onload = (event) => {
              const base64 = event.target?.result as string;
              setImageBase64(prev => {
                const combined = [...prev, base64];
                return combined.slice(0, 3);
              });
              cleanup();
            };
            reader.readAsDataURL(file);
          } catch (error) {
            toast.error(t('notifications.toast.failed_to_load_image'));
          }
        }
      };

      uploadBtn.onclick = () => fileInput.click();

      const cleanup = () => {
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(overlay);
        // Resume background color sync
        resumeBackgroundSync();
      };

      closeBtn.onclick = cleanup;

      recordBtn.onclick = async () => {
        recordBtn.disabled = true;
        recordBtn.style.opacity = '0.5';
        instructions.style.display = 'none';
        indicatorsContainer.style.opacity = '1';

        const capturedFrames: string[] = [];
        const capturedFlags = new Array(neededImages).fill(false);

        try {
          // Wait for video to be ready
          await new Promise(resolve => {
            if (video.readyState >= 2) resolve(null);
            else video.onloadeddata = () => resolve(null);
          });

          // Helper to trigger capture effects
          const triggerCaptureEffects = async (index: number) => {
            // Quick scan flash
            scanLine.classList.remove('scan-flash');
            void scanLine.offsetWidth; // Force reflow
            scanLine.classList.add('scan-flash');

            // Fill circle indicator
            const circle = indicators[index].querySelector('div') as HTMLElement;
            const text = indicators[index].querySelector('div:last-child') as HTMLElement;
            circle.style.backgroundColor = 'rgba(255,255,255,0.95)';
            circle.style.borderColor = 'rgba(255,255,255,0.95)';
            circle.style.transform = 'scale(1.15)';
            circle.style.boxShadow = '0 0 12px rgba(255,255,255,0.5)';
            setTimeout(() => { circle.style.transform = 'scale(1)'; circle.style.boxShadow = 'none'; }, 200);
            text.style.color = 'white';

            // Mark progress marker as captured
            if (progressMarkers[index]) {
              progressMarkers[index].classList.add('captured');
            }

            // Screen flash effect
            overlay.style.backgroundColor = 'rgba(255,255,255,0.3)';
            setTimeout(() => overlay.style.backgroundColor = 'black', 100);

            // Capture frame
            const frame = await captureFrame(video);
            capturedFrames.push(frame);
          };

          // Progress bar is the source of truth - use linear progress
          const startTime = performance.now();

          await new Promise<void>((resolve) => {
            const updateProgress = (currentTime: number) => {
              const elapsed = currentTime - startTime;
              const progress = Math.min(100, (elapsed / totalDuration) * 100);

              // Direct DOM update for smooth progress
              progressFill.style.width = `${progress}%`;

              // Check if we've hit any capture points
              for (let i = 0; i < captureTimes.length; i++) {
                if (!capturedFlags[i] && elapsed >= captureTimes[i]) {
                  capturedFlags[i] = true;
                  triggerCaptureEffects(i);
                }
              }

              if (elapsed >= totalDuration) {
                progressFill.style.width = '100%';
                resolve();
              } else {
                requestAnimationFrame(updateProgress);
              }
            };
            requestAnimationFrame(updateProgress);
          });

          // Add captured frames to state, strictly enforcing max 3 images
          setImageBase64(prev => {
            const combined = [...prev, ...capturedFrames];
            // Never allow more than 3 images
            return combined.slice(0, 3);
          });
          cleanup();

        } catch (error) {
          toast.error(t('quick_entry.camera.capture_error', { error: error instanceof Error ? error.message : 'Unknown error' }));
          cleanup();
        }
      };

      // Add buttons to the container
      buttonsContainer.appendChild(recordBtn);
      buttonsContainer.appendChild(quickSnapBtn);

      uiContainer.appendChild(indicatorsContainer);
      uiContainer.appendChild(progressContainer);
      uiContainer.appendChild(instructions);
      uiContainer.appendChild(buttonsContainer);
      overlay.appendChild(video);
      overlay.appendChild(cornerOverlay);
      overlay.appendChild(uiContainer);
      overlay.appendChild(flipCameraBtn);
      overlay.appendChild(closeBtn);
      overlay.appendChild(uploadBtn);
      overlay.appendChild(fileInput);
      document.body.appendChild(overlay);
      overlayAdded = true;

    } catch (error) {
      // Clean up stream if it was obtained
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      // Remove overlay if it was added
      if (overlayAdded && overlay) {
        document.body.removeChild(overlay);
      }
      // Always resume background sync on error
      resumeBackgroundSync();
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        toast.error(t('quick_entry.camera.access_denied'));
      } else {
        toast.error(t('quick_entry.camera.open_error', { error: error instanceof Error ? error.message : 'Unknown error' }));
      }
    }
  };

  // Cycle through loading phrases while AI is processing
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        setLoadingPhraseIndex(prev => (prev + 1) % loadingPhrases.length);
      }, 2000); // Change every 2 seconds
      return () => clearTimeout(timer);
    } else {
      setLoadingPhraseIndex(0);
    }
  }, [loading, loadingPhraseIndex]);

  // Typing animation effect
  useEffect(() => {
    // If user is typing, image is uploaded, or audio is recorded, don't show animation
    if (imageBase64.length > 0 || audioBase64 || text.length > 0) {
      setDisplayedText("");
      return;
    }

    const currentText = placeholderExamples[placeholderIndex];

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing forward
        if (charIndex < currentText.length) {
          setDisplayedText(currentText.slice(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        } else {
          // Finished typing, wait then start deleting
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        // Deleting backward
        if (charIndex > 0) {
          setDisplayedText(currentText.slice(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        } else {
          // Finished deleting, move to next phrase
          setIsDeleting(false);
          setPlaceholderIndex((prev) => (prev + 1) % placeholderExamples.length);
        }
      }
    }, isDeleting ? 40 : 100); // Faster when deleting

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, placeholderIndex, placeholderExamples, imageBase64, audioBase64, text]);

  // Wait for auth
  useEffect(() => {
    const initAuth = async () => {
      await supabase.auth.getSession();
      setAuthLoading(false);
    };
    initAuth();
  }, []);

  const handleClearAll = () => {
    setText("");
    setImageBase64([]);
    setAudioBase64(null);
    setAudioContentType(null);
    setImageMode(null);
    setShowImageModePicker(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Lock scroll when mobile enhanced UI is shown (but only when input is not focused)
  useEffect(() => {
    const showMobileEnhancedUI = isMobile && (text.trim().length > 0 || imageBase64.length > 0 || audioBase64);

    // When input is focused with an image or audio, scroll to keep content visible
    if (showMobileEnhancedUI && isInputFocused && (imageBase64.length > 0 || audioBase64)) {
      // Small delay to let the keyboard appear
      const timer = setTimeout(() => {
        textInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      return () => clearTimeout(timer);
    }

    // Only lock scroll if the enhanced UI is shown AND the input is not focused
    // When focused, allow scrolling so the keyboard doesn't hide the input
    if (showMobileEnhancedUI && !isInputFocused) {
      // Scroll back to top to ensure proper positioning before locking
      window.scrollTo(0, 0);

      // Lock scroll at top position
      document.body.style.position = 'fixed';
      document.body.style.top = '0px';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, 0);
      };
    }
  }, [isMobile, text, imageBase64, audioBase64, isInputFocused]);

  // Notify parent when AI input state changes
  useEffect(() => {
    const hasAIInput = text.trim().length > 0 || imageBase64.length > 0 || !!audioBase64;
    onAIInputChange?.(hasAIInput);
  }, [text, imageBase64, audioBase64, onAIInputChange]);

  // Notify parent when AI loading state changes
  useEffect(() => {
    onAILoadingChange?.(loading);
  }, [loading, onAILoadingChange]);

  // Get user's credits
  const { data: creditsData, refetch: refetchCredits } = useQuery({
    queryKey: ["user-credits", userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error("No user ID");
      }
      const { data, error } = await supabase
        .from("user_credits")
        .select("credits")
        .eq("user_id", userId)
        .single();
      if (error) {
        console.error("ERROR FETCHING CREDITS:", error);
        throw error;
      }
      return data;
    },
    enabled: !authLoading && !!userId,
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Get enabled metrics
  const { data: metricPrefs } = useQuery({
    queryKey: ["metric-preferences", userId],
    queryFn: async () => {
      const { data } = await supabase.from("metric_preferences").select("*").eq("user_id", userId).maybeSingle();
      return data;
    },
  });

  // Get custom metrics
  const { data: customMetrics } = useQuery({
    queryKey: ["custom-metrics", userId],
    queryFn: async () => {
      const { data } = await supabase.from("custom_metrics").select("*").eq("user_id", userId);
      return data || [];
    },
  });

  // Check if user has any metrics enabled (built-in or custom)
  const hasNoBuiltInMetrics = !metricPrefs || (
    (!metricPrefs.nutrition_metrics || metricPrefs.nutrition_metrics.length === 0) &&
    (!metricPrefs.activity_sleep_metrics || metricPrefs.activity_sleep_metrics.length === 0) &&
    (!metricPrefs.results_metrics || metricPrefs.results_metrics.length === 0)
  );
  const hasNoMetrics = hasNoBuiltInMetrics && (!customMetrics || customMetrics.length === 0);

  // Load all enabled metrics
  const { data: allEnabledMetrics = [] } = useQuery({
    queryKey: ["enabled-metrics-quick-entry", userId],
    queryFn: async () => {
      return await buildEnabledMetrics();
    },
  });

  const buildEnabledMetrics = async () => {
    const metrics: any[] = [];

    const normalizeKey = (k: string) => {
      switch (k) {
        case "miles":
          return "miles_ran";
        case "water":
          return "water_gallons";
        case "appearance":
          return "self_appearance_rating";
        case "body_fat":
          return "body_fat_percentage";
        default:
          return k;
      }
    };

    // Fetch all metrics from database
    const allMetrics = await getAllMetrics();
    const metricDefs = Object.fromEntries(allMetrics.map(m => [m.key, m]));

    // Built-in metrics with emojis (including boolean metrics so AI can suggest them)
    const nutritionMetrics = [
      metricDefs.calories,
      metricDefs.protein,
      metricDefs.carbs,
      metricDefs.fiber,
      metricDefs.added_sugars,
      metricDefs.water_gallons,
      metricDefs.ate_healthy,
      metricDefs.low_sugars,
      metricDefs.fasted,
    ].filter(Boolean);

    const activityMetrics = [
      metricDefs.miles_ran,
      metricDefs.gym_time,
      metricDefs.sleep,
      // Exclude: github_commits (auto-synced, not user-trackable)
    ].filter(Boolean);

    const resultsMetrics = [
      metricDefs.body_weight,
      metricDefs.body_fat_percentage,
      metricDefs.mood,
      metricDefs.self_appearance_rating,
    ].filter(Boolean);

    // ALWAYS include ALL built-in numeric metrics (not just enabled ones)
    // This allows AI to suggest from the full range of trackable metrics
    // Excludes: boolean metrics, github_commits, and healthkit metrics
    metrics.push(...nutritionMetrics, ...activityMetrics, ...resultsMetrics);

    // Add custom metrics with emojis (only those that are enabled/created by user)
    customMetrics?.forEach((cm) => {
      metrics.push({
        key: `${cm.metric_key}_value`, // Use actual metric_key from database, not array index
        name: cm.metric_name,
        unit: cm.metric_unit || "", // Ensure unit is never null/undefined
        emoji: cm.metric_emoji,
        type: cm.metric_type, // Include type so AI knows if it's boolean or numeric
      });
    });

    return metrics;
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      // If user cancelled the image selection, close the camera tips dialog
      setShowCameraTips(false);
      return;
    }

    try {
      const compressedBase64 = await compressImageForAnalysis(file);

      // Close camera tips dialog now that we have an image
      setShowCameraTips(false);

      // If progress mode, navigate directly to create-snap
      // Free users can now take the photo, but will be prompted to upgrade when posting
      if (imageMode === 'progress') {
        navigate("/create-snap", {
          state: {
            selectedDate,
            initialImage: compressedBase64,
            initialText: text
          }
        });
        return;
      }

      // Otherwise, append to AI mode images array (max 3)
      setImageBase64(prev => {
        if (prev.length >= 3) {
          toast.info(t('notifications.toast.maximum_3_images_allowed'));
          return prev;
        }
        return [...prev, compressedBase64];
      });

      // Auto-focus the text input if it's empty
      if (!text.trim() && textInputRef.current) {
        textInputRef.current.focus();
      }

      // Reset file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Image compression error:", error);
      toast.error(t('notifications.toast.failed_to_process_image'));
      // Close the dialog on error too
      setShowCameraTips(false);
    }
  };

  const handleAudioRecording = async (e?: React.MouseEvent) => {
    // Prevent any default behavior or form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isRecording) return;

    setIsRecording(true);

    // Show appropriate message for native vs browser recording
    if (window.NativelyAudioRecorder) {
      toast.info(t('notifications.toast.opening_audio_recorder'), { duration: 2000 });
    }

    try {
      const result = await recordAudio();

      if (!result.success) {
        if (result.error === 'Recording cancelled by user') {
          toast.info(t('notifications.toast.recording_cancelled'));
        } else {
          toast.error(result.error || "Failed to record audio");
        }
        return;
      }

      if (!result.data) {
        toast.error(t('notifications.toast.no_audio_data_received'));
        return;
      }

      // Native recorder is already clamped to 15s max, backend will validate
      setAudioBase64(result.data.base64);
      setAudioContentType(result.data.content_type);

      // Clear text input when audio is recorded (audio replaces text)
      setText("");

      toast.success(t('notifications.toast.audio_recorded'), {
        duration: 2000,
      });

      // Don't auto-focus on mobile after native voice input to prevent unwanted form submission
      // User can manually tap the input if they want to add a description
      if (!window.NativelyAudioRecorder && textInputRef.current) {
        textInputRef.current.focus();
      }
    } catch (error) {
      console.error("Audio recording error:", error);
      toast.error(t('notifications.toast.an_error_occurred_during_recording'));
    } finally {
      setIsRecording(false);
    }
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);

    // When dialog closes, check if there are queued results to show
    if (!open) {
      setText("");
      setImageBase64([]);  // Reset to empty array, not null
      setAudioBase64(null);
      setAudioContentType(null);
      setSuggestedValues(null);

      // Reset file input to allow selecting the same image again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Process next item in the scan results queue
      setScanResultsQueue(prev => {
        if (prev.length > 0) {
          const [nextResult, ...rest] = prev;
          // Use setTimeout to avoid state update conflicts
          setTimeout(() => {
            setSuggestedValues(nextResult.suggestedValues);
            setFreeFixAvailable(nextResult.freeFixAvailable);
            setDialogOpen(true);
          }, 300); // Small delay for smooth transition between dialogs
          return rest;
        } else {
          // Queue is empty - check if we should trigger the tutorial
          // Use setTimeout to ensure state has settled
          setTimeout(() => {
            if (pendingTutorialTriggerRef.current && activeScansCountRef.current === 0) {
              console.log("[QuickEntryInput] All dialogs done (from handleDialogClose) - triggering tutorial");
              pendingTutorialTriggerRef.current();
              setPendingTutorialTrigger(null);
            }
          }, 400); // Slightly longer delay to ensure emoji explosion has completed
        }
        return prev;
      });
    }
  };

  const handleProgressPicSubmit = () => {
    // Free users can now take the photo, but will be prompted to upgrade when posting
    // Navigate to create-snap page with the image
    navigate("/create-snap", {
      state: {
        selectedDate,
        initialImage: imageBase64[0],
        initialText: text
      }
    });
  };

  // Check if input contains a question mark (indicates question mode vs logging mode)
  const isQuestionMode = (inputText: string, hasImages: boolean, hasAudio: boolean): boolean => {
    // Only text-only queries can be questions - no images or audio
    if (hasImages || hasAudio) return false;
    if (!inputText.trim()) return false;
    return inputText.includes('?');
  };

  // Compute whether we're in question mode for the current input
  const currentlyInQuestionMode = isQuestionMode(text, imageBase64.length > 0, !!audioBase64);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If progress mode is selected, use the progress pic submit instead
    if (imageMode === 'progress' && imageBase64.length > 0) {
      handleProgressPicSubmit();
      return;
    }

    if (!text.trim() && imageBase64.length === 0 && !audioBase64) return;
    if (isRecording) return; // Prevent submissions during recording
    hapticToggle();
    
    // Allow up to 3 concurrent scans (queueing)
    const MAX_CONCURRENT_SCANS = 3;
    if (activeScansCount >= MAX_CONCURRENT_SCANS) {
      toast.info(t('notifications.toast.max_scans_queued') || 'Maximum scans queued. Please wait.');
      return;
    }

    // Check if this is a question (has ?) - route to data insights instead
    const hasImages = imageBase64.length > 0;
    const hasAudio = !!audioBase64;
    if (isQuestionMode(text.trim(), hasImages, hasAudio)) {
      // Data insights mode - 0.5 credits (5 in tenths)
      const insightsCreditCost = 5;
      
      if (!creditsData || creditsData.credits < insightsCreditCost) {
        if (isPremium) {
          toast.error(t('notifications.toast.out_of_credits'));
          navigate("/credits-success");
        } else {
          toast.error("You're out of credits. Upgrade for unlimited AI.");
          navigate("/upgrade", { state: { fromCredits: true } });
        }
        return;
      }
      
      // Open data insights dialog with the question
      setInitialAnalyticalQuestion(text.trim());
      setDataInsightsOpen(true);
      setText(""); // Clear input
      return;
    }

    // Determine credit cost in tenths (3 = 0.3, 10 = 1, 13 = 1.3, 15 = 1.5 for audio)
    let creditCost = 3; // Base cost for text only
    if (audioBase64) {
      creditCost = (imageBase64.length > 0 || text.trim()) ? 18 : 15; // Audio + image/text or just audio
    } else if (imageBase64.length > 0) {
      creditCost = text.trim() ? 13 : 10; // Image + text or just image
    }

    // If premium status is still loading, don't allow submit yet
    if (isPremium === undefined) {
      console.log("[QuickEntry] Premium status undefined - waiting for profile to load...");
      toast.info(t('notifications.toast.loading_your_account_details'));
      return;
    }

    console.log("Quick Entry - Credits check:", {
      userCredits: creditsData?.credits,
      isPremium,
      isPremiumType: typeof isPremium,
      creditCost,
      hasEnough: creditsData && creditsData.credits >= creditCost,
    });

    // Check credits
    if (!creditsData || creditsData.credits < creditCost) {
      console.error("Insufficient credits on frontend check", {
        isPremium,
        creditsData,
        creditCost,
        isPremiumType: typeof isPremium,
        isPremiumValue: isPremium
      });
      if (isPremium) {
        toast.error(t('notifications.toast.out_of_credits'));
        navigate("/credits-success");
      } else {
        toast.error("You're out of credits. Upgrade for unlimited AI.");
        navigate("/upgrade", { state: { fromCredits: true } });
      }
      return;
    }

    // Track this scan and capture current input state for this specific scan
    const currentText = text;
    const currentImageBase64 = [...imageBase64];
    const currentAudioBase64 = audioBase64;
    const currentAudioContentType = audioContentType;
    
    // Increment active scans and set loading state
    setActiveScansCount(prev => prev + 1);
    setLoading(true);
    setProgressSteps([]); // Reset progress steps
    setShowAddAnother(false); // Reset to show loading UI for new scan
    
    // Create an AbortController for this scan so it can be cancelled
    const abortController = new AbortController();
    scanAbortControllersRef.current.push(abortController);
    
    // DON'T clear input yet - keep it visible until user clicks "Log another item"
    // Input will be cleared when setShowAddAnother(true) is called
    
    try {
      const enabledMetrics = await buildEnabledMetrics();

      // Log to verify mood and appearance are included
      console.log('[QuickEntry] Enabled metrics being sent to AI:',
        enabledMetrics.map(m => m.key).join(', ')
      );

      // Log custom metrics with names to verify they're being sent
      const customMetricsInRequest = enabledMetrics.filter(m => m.key.startsWith('custom_'));
      console.log('[QuickEntry] Custom metrics with names:', customMetricsInRequest);

      // Get auth token for the request
      const { data: sessionData } = await supabase.auth.getSession();
      const authToken = sessionData?.session?.access_token;

      // Detect where the request is coming from (native app vs web browser + OS)
      // so the backend can log it. Fails soft - the server falls back to the
      // User-Agent header if this isn't sent.
      let clientPlatform: { type: 'native' | 'web'; os?: string; osVersion?: string; device?: string } | undefined;
      try {
        const detection = await detectNativeApp();
        clientPlatform = {
          type: detection.isNative ? 'native' : 'web',
          os: detection.platformInfo?.osName || (isIOS() ? 'iOS' : isAndroid() ? 'Android' : 'web'),
          osVersion: detection.platformInfo?.osVersion,
          device: detection.platformInfo?.device,
        };
      } catch (e) {
        console.warn('[QuickEntry] Failed to detect client platform:', e);
      }

      // Use SSE streaming for real-time progress updates
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const requestBody = {
        text: currentText.slice(0, 100), // Max 100 chars - use captured value
        enabledMetrics,
        images: currentImageBase64.length > 0 ? currentImageBase64 : undefined,
        audioBase64: currentAudioBase64 || undefined,
        audioContentType: currentAudioContentType || undefined,
        fingerSizeMm: fingerSizeMm || undefined,
        clientPlatform,
        stream: true, // Enable SSE streaming
      };
      
      const sseResponse = await fetch(`${supabaseUrl}/functions/v1/parse-quick-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestBody),
        signal: abortController.signal,
      });
      
      // Check if we got an SSE response (streaming)
      const contentType = sseResponse.headers.get('content-type') || '';
      
      let data: any = null;
      
      if (contentType.includes('text/event-stream')) {
        // Handle SSE streaming response
        const reader = sseResponse.body?.getReader();
        const decoder = new TextDecoder();
        
        if (reader) {
          let buffer = '';
          
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            buffer += decoder.decode(value, { stream: true });
            
            // Process complete SSE events (separated by double newlines)
            const events = buffer.split('\n\n');
            buffer = events.pop() || ''; // Keep incomplete event in buffer
            
            for (const eventText of events) {
              if (!eventText.trim()) continue;
              
              const lines = eventText.split('\n');
              let eventType = '';
              let eventData = '';
              
              for (const line of lines) {
                if (line.startsWith('event: ')) {
                  eventType = line.slice(7);
                } else if (line.startsWith('data: ')) {
                  eventData = line.slice(6);
                }
              }
              
              if (eventType && eventData) {
                try {
                  const parsed = JSON.parse(eventData);
                  
                  if (eventType === 'progress') {
                    // Add progress step to the list (accumulate, don't replace)
                    setProgressSteps(prev => {
                      // For ai_update stage, allow multiple with different messages
                      // For other stages, only add if it's a new stage
                      if (parsed.stage === 'ai_update') {
                        // Check if this exact message already exists
                        if (prev.some(p => p.stage === 'ai_update' && p.message === parsed.message)) return prev;
                        return [...prev, { stage: `ai_update_${Date.now()}`, message: parsed.message, detail: parsed.detail }];
                      }
                      if (prev.some(p => p.stage === parsed.stage)) return prev;
                      return [...prev, { stage: parsed.stage, message: parsed.message, detail: parsed.detail }];
                    });
                  } else if (eventType === 'result') {
                    // Final result received
                    data = parsed;
                  } else if (eventType === 'error') {
                    throw new Error(parsed.error || 'Unknown streaming error');
                  }
                } catch (e) {
                  console.error('[QuickEntry] Failed to parse SSE event:', e, eventData);
                }
              }
            }
          }
        }
      } else {
        // Fallback: non-streaming JSON response
        if (!sseResponse.ok) {
          const errorData = await sseResponse.json().catch(() => ({}));
          throw { error: { message: sseResponse.statusText }, data: errorData };
        }
        data = await sseResponse.json();
      }
      
      // Handle case where streaming flag was on but we got partial JSON response 
      // (the streaming mode returns a simplified response)
      if (data?.streaming && !data?.suggestedValues) {
        // Convert streaming response format to expected format
        const items = data.items || [];
        const suggestedValues: Record<string, number> = {};
        
        // Build suggestedValues from items
        for (const item of items) {
          const qty = item.quantity || 1;
          for (const v of (item.values || [])) {
            const key = v.key;
            const val = typeof v.value_per_item === 'number' ? v.value_per_item * qty : v.value_per_item;
            suggestedValues[key] = (suggestedValues[key] || 0) + (typeof val === 'number' ? val : 0);
          }
        }
        
        data = {
          suggestedValues,
          comment: data.message || null,
          healthierReason: null,
          suggestedCustomMetrics: data.suggested_custom_metrics || null,
          items: data.items || null,
          references: data.references || null,
          calorie_breakdown: null,
          inputSummary: null,
          outputCalculationExplanation: null,
        };
      }

      // Parse error response body if present
      let errorBody = null;
      if (!data) {
        errorBody = { error: 'No response data received' };
      }

      // Check for quarantine/suspension with release date (check this FIRST before other errors)
      const quarantineData = errorBody || data;
      if (quarantineData?.reason === "quarantine" && quarantineData?.until) {
        const releaseDate = new Date(quarantineData.until);
        const formattedDate = releaseDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        });
        toast.error(`Your account was flagged and is suspended until ${formattedDate}`);
        return;
      }

      // Check for specific insufficient credits error (402)
      const creditsError = errorBody?.error || data?.error;
      if (creditsError === "insufficient_credits") {
        const isPremiumUser = (errorBody?.premium || data?.premium) ?? isPremium ?? false;
        console.error("Insufficient credits from backend", {
          isPremium,
          isPremiumUser,
          backendPremium: data?.premium,
          isPremiumType: typeof isPremium,
          isPremiumUserType: typeof isPremiumUser
        });
        if (isPremiumUser) {
          toast.error(t('notifications.toast.out_of_credits'));
          navigate("/credits-success");
        } else {
          toast.error("You're out of credits. Upgrade for daily refills.");
          navigate("/upgrade", { state: { fromCredits: true } });
        }
        return;
      }

      // Handle other errors
      if (errorBody || data?.error) {
        console.error("Function error:", errorBody || data?.error);

        // Check for rate limit or validation errors
        const errorData = errorBody?.error || data?.error || "";
        const budgetExceeded = errorBody?.budget_exceeded || data?.budget_exceeded || false;

        // Check for quarantine/suspension text in error data
        if (typeof errorData === 'string' && (errorData.includes("quarantine") || errorData.includes("policy violations"))) {
          // Fallback for quarantine without structured data
          toast.error(t('notifications.toast.your_account_was_flagged_and_is_suspended_please_c'));
        } else if (typeof errorData === 'string' && (errorData.includes("suspended") || errorData.includes("permanently"))) {
          toast.error(t('notifications.toast.your_account_is_suspended_please_contact_support'));
        } else if (budgetExceeded || (typeof errorData === 'string' && errorData.includes("very busy right now"))) {
          toast.error("We're very busy right now. Please upgrade to get instant access or try again later.");
        } else if (typeof errorData === 'string' && errorData.includes("Rate limit")) {
          toast.error(t('notifications.toast.too_many_requests_please_wait_a_moment'));
        } else if (typeof errorData === 'string' && (errorData.includes("too large") || errorData.includes("max"))) {
          toast.error(errorData);
        } else if (typeof errorData === 'string' && errorData.includes("AI_TIMEOUT")) {
          toast.error(t('notifications.toast.request_timed_out_please_try_again'));
        } else if (typeof errorData === 'string' && errorData.includes("AI_NETWORK_ERROR")) {
          toast.error(t('notifications.toast.network_error_please_check_your_connection'));
        } else if (typeof errorData === 'string' && errorData.includes("Content could not be processed")) {
          toast.error(t('notifications.toast.content_blocked_by_safety_filters_try_different_in'));
        } else if (errorData) {
          // Show the actual error message from the server
          toast.error(typeof errorData === 'string' ? errorData : 'An error occurred');
        } else {
          toast.error(t('notifications.toast.an_unexpected_error_occurred_please_try_again'));
        }
        return;
      }

      if (!data?.suggestedValues) {
        toast.error(t('notifications.toast.failed_to_parse_entry'));
        return;
      }

      console.log('[QuickEntryInput] Response data:', data);
      console.log('[QuickEntryInput] Suggested values:', data?.suggestedValues);
      console.log('[QuickEntryInput] Suggested values keys:', Object.keys(data?.suggestedValues || {}));

      // Debug logging
      console.log("[QuickEntryInput] Response data:", {
        comment: data.comment,
        healthierReason: data.healthierReason,
        suggestedValues: data.suggestedValues,
        suggestedCustomMetrics: data.suggestedCustomMetrics,
        items: data.items,
        references: data.references,
        freeFixAvailable: data.freeFixAvailable
      });

      // Track if a free fix is available (granted when credits were spent)
      const scanFreeFixAvailable = data.freeFixAvailable === true;

      // Pass AI comment, healthier reason, suggested custom metrics, items, and references to dialog
      const resultValues = {
        ...data.suggestedValues,
        aiComment: data.comment || null,
        healthierReason: data.healthierReason || null,
        suggestedCustomMetrics: data.suggestedCustomMetrics || null,
        items: data.items || null,
        references: data.references || null,
        calorie_breakdown: data.calorie_breakdown || null,
        inputSummary: data.inputSummary || null,
        outputCalculationExplanation: data.outputCalculationExplanation || null,
      };

      // If a dialog is already open (including FixInChat), queue this result for later
      // Use refs instead of state because state values are stale in async closures
      if (dialogOpenRef.current || fixInChatOpenRef.current) {
        setScanResultsQueue(prev => [...prev, {
          suggestedValues: resultValues,
          originalText: currentText,
          freeFixAvailable: scanFreeFixAvailable,
        }]);
      } else {
        // Animate progress bar to 100% before showing dialog
        await animateProgressToComplete();
        setScanProgress(0);
        scanProgressRef.current = 0;
        setSuggestedValues(resultValues);
        setFreeFixAvailable(scanFreeFixAvailable);
        setDialogOpen(true);
      }
      // Note: State will be cleared when dialog closes via handleDialogClose

      // Invalidate credits to update the display
      queryClient.invalidateQueries({ queryKey: ["user-credits", userId] });
    } catch (error: any) {
      // Don't show error toast if the request was intentionally aborted (user cancelled)
      if (error?.name === 'AbortError') {
        console.log('[QuickEntry] Scan aborted by user');
      } else {
        console.error("Caught error:", error);
        toast.error(t('notifications.toast.an_unexpected_error_occurred'));
      }
    } finally {
      // Remove this controller from the active list
      scanAbortControllersRef.current = scanAbortControllersRef.current.filter(c => c !== abortController);
      // If aborted, don't touch state — cancel handler already reset everything
      if (abortController.signal.aborted) return;
      // Decrement active scans count
      setActiveScansCount(prev => {
        const newCount = Math.max(0, prev - 1);
        // Only turn off loading when all scans are complete
        if (newCount === 0) {
          setLoading(false);
          setShowAddAnother(false); // Reset add another mode when all scans complete
        }
        return newCount;
      });
      // Clear progress steps after a short delay for smooth transition
      setTimeout(() => setProgressSteps([]), 500);
    }
  };

  // Check if we should show mobile enhanced UI
  // Show when there's content, OR when loading/scans are active
  const hasContent = isMobile && (text.trim().length > 0 || imageBase64.length > 0 || audioBase64);
  const hasActiveScans = activeScansCount > 0;
  const showMobileEnhancedUI = (hasContent || hasActiveScans) && (showOverlayMode || loading);

  // Progress bar animation: fills to ~96% over 11s with exponential slowdown, then crawls
  useEffect(() => {
    if (hasActiveScans && !showAddAnother) {
      // Start progress animation
      scanStartTimeRef.current = performance.now();
      scanProgressRef.current = 0;
      setScanProgress(0);

      const animate = () => {
        const elapsed = performance.now() - (scanStartTimeRef.current || performance.now());
        const duration = 19000; // 19 seconds to reach ~96%
        const t = Math.min(elapsed / duration, 1);
        // Ease-out curve that decelerates sharply: fast start, very slow end
        let progress: number;
        if (t < 1) {
          // Strong ease-out: 1 - (1-t)^3 mapped to 0-96%
          progress = (1 - Math.pow(1 - t, 3)) * 96;
        } else {
          // Beyond 11s: crawl from 96% asymptotically toward 98%
          const overtime = elapsed - duration;
          // Each additional 10s only adds ~1% more
          progress = 96 + 2 * (1 - Math.exp(-overtime / 20000));
        }
        scanProgressRef.current = progress;
        setScanProgress(progress);
        scanProgressRafRef.current = requestAnimationFrame(animate);
      };

      scanProgressRafRef.current = requestAnimationFrame(animate);

      return () => {
        if (scanProgressRafRef.current) {
          cancelAnimationFrame(scanProgressRafRef.current);
          scanProgressRafRef.current = null;
        }
      };
    } else if (!hasActiveScans) {
      // Reset when no active scans
      scanStartTimeRef.current = null;
    }
  }, [hasActiveScans, showAddAnother]);

  // Lerp progress bar to 100% and return a promise that resolves when done
  const animateProgressToComplete = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      // Stop the ongoing animation
      if (scanProgressRafRef.current) {
        cancelAnimationFrame(scanProgressRafRef.current);
        scanProgressRafRef.current = null;
      }

      const startVal = scanProgressRef.current;
      const startTime = performance.now();
      const lerpDuration = 400; // 400ms lerp to 100%

      const lerpAnimate = () => {
        const elapsed = performance.now() - startTime;
        const t = Math.min(elapsed / lerpDuration, 1);
        // Smooth ease-out for the final fill
        const eased = 1 - Math.pow(1 - t, 2);
        const progress = startVal + (100 - startVal) * eased;
        scanProgressRef.current = progress;
        setScanProgress(progress);

        if (t < 1) {
          scanProgressRafRef.current = requestAnimationFrame(lerpAnimate);
        } else {
          // Hold at 100% briefly then resolve
          setTimeout(() => {
            resolve();
          }, 200);
        }
      };

      scanProgressRafRef.current = requestAnimationFrame(lerpAnimate);
    });
  }, []);

  // Update overlay mode based on focus and content
  useEffect(() => {
    // Always show overlay when loading or there are active scans
    if (loading || hasActiveScans) {
      setShowOverlayMode(true);
      return;
    }

    // Always show overlay when image or audio is attached
    if (imageBase64.length > 0 || audioBase64) {
      setShowOverlayMode(true);
      return;
    }

    if (hasContent && isInputFocused) {
      setShowOverlayMode(true);
    } else if (!isInputFocused && hasContent) {
      // Small delay to allow the blur event to complete
      const timer = setTimeout(() => {
        setShowOverlayMode(false);
      }, 100);
      return () => clearTimeout(timer);
    } else if (!hasContent && !hasActiveScans) {
      setShowOverlayMode(false);
      setShowAddAnother(false); // Reset when overlay closes
    }
  }, [hasContent, isInputFocused, loading, imageBase64, audioBase64, hasActiveScans]);

  // Broadcast mobile backdrop state to other components
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('mobile-backdrop-state', {
      detail: { active: showMobileEnhancedUI }
    }));
  }, [showMobileEnhancedUI]);

  return (
    <>
      <style>{`
        @keyframes gradient-sweep {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes shimmer-glow {
          0%, 100% { 
            box-shadow: 0 0 8px 1px rgba(34, 197, 94, 0.2), 
                        0 0 12px 2px rgba(34, 197, 94, 0.1),
                        inset 0 1px 0 rgba(255, 255, 255, 0.08);
          }
          50% { 
            box-shadow: 0 0 12px 2px rgba(34, 197, 94, 0.25), 
                        0 0 16px 2px rgba(34, 197, 94, 0.12),
                        inset 0 1px 0 rgba(255, 255, 255, 0.12);
          }
        }
        
        @keyframes cursor-blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        
        @keyframes hexagon-pulse {
          0%, 100% { 
            stroke-dashoffset: 0;
            opacity: 1;
          }
          50% { 
            stroke-dashoffset: 600;
            opacity: 0.6;
          }
        }
        
        @keyframes button-pulse {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 8px 24px rgba(34, 197, 94, 0.3);
          }
          50% { 
            transform: scale(1.05);
            box-shadow: 0 12px 32px rgba(34, 197, 94, 0.5);
          }
        }
        
        @keyframes ripple {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
        
        .ai-glow-input {
          animation: shimmer-glow 2.5s ease-in-out infinite;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-color: rgba(34, 197, 94, 0.4) !important;
        }
        
        .ai-glow-input:hover {
          border-color: rgba(34, 197, 94, 0.6) !important;
        }
        
        .ai-glow-input:focus {
          animation: none;
          box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.4), 
                      inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
          border-color: rgba(34, 197, 94, 0.8) !important;
        }
        
        .mobile-gradient-input::placeholder {
          background: linear-gradient(90deg, 
            rgba(34, 197, 94, 0.3),
            rgba(16, 185, 129, 0.35),
            rgba(5, 150, 105, 0.32),
            rgba(34, 197, 94, 0.3)
          );
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradient-sweep 3s ease-in-out infinite;
          font-weight: 400;
          letter-spacing: 0.015em;
        }
        
        .dark .mobile-gradient-input::placeholder,
        .system .mobile-gradient-input::placeholder {
          background: linear-gradient(90deg, 
            rgba(34, 197, 94, 0.4),
            rgba(16, 185, 129, 0.45),
            rgba(5, 150, 105, 0.42),
            rgba(34, 197, 94, 0.4)
          );
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .typing-cursor::after {
          content: '|';
          animation: cursor-blink 1s infinite;
          margin-left: 2px;
          font-weight: 400;
        }
        
        .mobile-gradient-input {
          background: linear-gradient(90deg, 
            hsl(0 0% 20%), 
            hsl(0 0% 10%), 
            hsl(0 0% 20%)
          );
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradient-sweep 3s ease-in-out infinite;
          font-weight: 500;
          transition: all 300ms ease;
        }
        
        .mobile-gradient-input.active-input {
          -webkit-text-fill-color: white !important;
          background: none !important;
          animation: none !important;
          color: white !important;
        }
        
        .mobile-gradient-input.active-input:focus {
          -webkit-text-fill-color: white !important;
          color: white !important;
        }
        
        .mobile-gradient-input.active-input::placeholder {
          background: none !important;
          -webkit-text-fill-color: rgba(209, 213, 219, 0.7) !important;
          color: rgba(209, 213, 219, 0.7) !important;
          animation: none !important;
        }
        
        .dark .mobile-gradient-input,
        .system .mobile-gradient-input {
          background: linear-gradient(90deg, 
            hsl(0 0% 90%), 
            hsl(0 0% 98%), 
            hsl(0 0% 90%)
          );
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
        }
        
        .dark .mobile-gradient-input.active-input,
        .system .mobile-gradient-input.active-input {
          -webkit-text-fill-color: white !important;
          background: none !important;
        }
        
        @property --angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }

        @keyframes rotate-gradient {
          from {
            --angle: 180deg;
            opacity: 1;
          }
          90% {
            --angle: 900deg;
            opacity: 1;
          }
          to {
            --angle: 900deg;
            opacity: 0;
          }
        }

        .mobile-input-wrapper {
          position: relative;
        }

        .mobile-input-wrapper::after {
          content: '';
          position: absolute;
          inset: -2px;
          border-radius: 1.125rem;
          padding: 2px;
          background: conic-gradient(
            from var(--angle),
            transparent 0deg,
            transparent 320deg,
            rgba(34, 197, 94, 0.4) 340deg,
            rgba(34, 197, 94, 0.8) 360deg
          );
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 1;
          transition: opacity 0.3s ease;
          animation: rotate-gradient 6s linear forwards;
          pointer-events: none;
        }

        .mobile-input-wrapper:focus-within::after {
          opacity: 0;
        }
        
        .mobile-input-wrapper::before {
          content: '';
          position: absolute;
          inset: -2px;
          border-radius: 1.125rem;
          padding: 2px;
          background: linear-gradient(135deg, 
            rgba(34, 197, 94, 0.3),
            rgba(16, 185, 129, 0.2),
            rgba(5, 150, 105, 0.25),
            rgba(34, 197, 94, 0.3)
          );
          background-size: 300% 300%;
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 0.3s ease;
          animation: gradient-sweep 4s ease infinite;
          pointer-events: none;
        }
        
        .mobile-input-wrapper:focus-within::before {
          opacity: 1;
        }
        
        .mobile-backdrop {
          backdrop-filter: blur(8px);
          background: rgba(0, 0, 0, 0.6);
          transition: opacity 400ms cubic-bezier(0.4, 0, 0.2, 1), backdrop-filter 400ms cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .mobile-backdrop-enter {
          opacity: 0;
          backdrop-filter: blur(0px);
        }
        
        .generate-button-loading {
          animation: button-pulse 2s ease-in-out infinite;
        }
        
        .ripple-ring {
          animation: ripple 1.5s cubic-bezier(0, 0.2, 0.8, 1) infinite;
        }
        
        .mobile-button-enter {
          animation: slideUpFade 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        @keyframes slideUpFade {
          0% {
            opacity: 0;
            transform: translateY(20px) scale(0.9);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .hexagon-preview-enter {
          animation: hexagonFadeIn 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        @keyframes hexagonFadeIn {
          0% {
            opacity: 0;
            transform: scale(0.8) translateY(-10px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>

      {/* Mobile Backdrop - shown when text or image is present */}
      {showMobileEnhancedUI && (
        <div
          className="fixed inset-0 mobile-backdrop z-40 pointer-events-none"
          style={{
            animation: 'fadeIn 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards'
          }}
        />
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            backdrop-filter: blur(0px);
          }
          to {
            opacity: 1;
            backdrop-filter: blur(8px);
          }
        }
      `}</style>

      <form onSubmit={handleSubmit} className={`relative flex-1 max-w-lg flex flex-col items-center gap-4 ${showMobileEnhancedUI ? 'z-[60]' : ''}`} data-component="quick-entry-input">
        {/* Try This First Tooltip when user has no metrics on mobile */}
        {showStartHereTooltip && isMobile && !showMobileEnhancedUI && (
          <div className="absolute -top-14 left-0 z-30 animate-bounce pointer-events-none">
            <div className="relative bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 text-white px-4 py-2.5 rounded-xl shadow-lg">
              <div className="flex items-center gap-2 text-sm font-bold whitespace-nowrap">
                <Sparkles className="w-4 h-4 animate-pulse" />
                {t('quick_entry.try_first')}
              </div>
              <div className="absolute -bottom-1 w-3 h-3 bg-fuchsia-400 rotate-45 left-6 -z-10" />
            </div>
          </div>
        )}
        {/* Start Here Tooltip for desktop */}
        {showStartHereTooltip && !isMobile && (
          <div className="absolute -top-16 z-40 animate-bounce pointer-events-none right-0">
            <div className="relative bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl shadow-lg">
              <div className="flex items-center gap-2 text-sm font-semibold whitespace-nowrap">
                <Sparkles className="w-4 h-4" />
                {t('quick_entry.start_here')}
              </div>
              <div className="absolute -bottom-1.5 w-3 h-3 bg-emerald-500 rotate-45 right-6" />
            </div>
          </div>
        )}

        {/* Image Preview with Hexagon Mask - Mobile Only */}
        {showMobileEnhancedUI && imageBase64.length > 0 && (
          <div className="relative flex items-center justify-center gap-3 mx-auto mb-4">
            {imageBase64.map((img, index) => (
              <div key={index} className={`relative ${imageBase64.length === 1 ? 'w-32 h-32' : 'w-24 h-24'} hexagon-preview-enter`} style={{ animationDelay: `${index * 100}ms` }}>
                {/* Hexagon Container */}
                <div className="relative w-full h-full">
                  {/* SVG with mask for rounded hexagon */}
                  <svg
                    className="w-full h-full"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <clipPath id={`hexClip${index}`}>
                        <path d="M 32 2 L 68 2 Q 70 2 71 3.5 L 97 48 Q 98 50 97 52 L 71 96.5 Q 70 98 68 98 L 32 98 Q 30 98 29 96.5 L 3 52 Q 2 50 3 48 L 29 3.5 Q 30 2 32 2 Z" />
                      </clipPath>
                    </defs>
                    <image
                      href={img}
                      width="100"
                      height="100"
                      preserveAspectRatio="xMidYMid slice"
                      clipPath={`url(#hexClip${index})`}
                      className={(loading && !showAddAnother) ? "ai-loading-image" : ""}
                    />
                  </svg>

                  {/* Remove button - only show on last image when not loading or when adding another */}
                  {(!loading || showAddAnother) && index === imageBase64.length - 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        setImageBase64(prev => prev.slice(0, -1));
                      }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-white/90 text-gray-600 rounded-full flex items-center justify-center shadow-lg hover:bg-red-500 hover:text-white transition-all z-10 backdrop-blur-sm"
                    >
                      <svg width="10" height="10" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}

                  {/* Hexagon Border with Pulse Animation when loading */}
                  {loading && !showAddAnother && (
                    <svg
                      className="absolute -inset-1 w-[calc(100%+8px)] h-[calc(100%+8px)]"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                    >
                      <defs>
                        <linearGradient id={`hexGradient${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="rgb(34, 197, 94)" />
                          <stop offset="50%" stopColor="rgb(16, 185, 129)" />
                          <stop offset="100%" stopColor="rgb(5, 150, 105)" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M 32 2 L 68 2 Q 70 2 71 3.5 L 97 48 Q 98 50 97 52 L 71 96.5 Q 70 98 68 98 L 32 98 Q 30 98 29 96.5 L 3 52 Q 2 50 3 48 L 29 3.5 Q 30 2 32 2 Z"
                        fill="none"
                        stroke={`url(#hexGradient${index})`}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeDasharray="150 250"
                        className="animate-[hexagon-pulse_2.5s_ease-in-out_infinite]"
                        style={{ animationDelay: `${index * 300}ms` }}
                      />
                    </svg>
                  )}
                </div>
              </div>
            ))}

            {/* Plus button to add more images - hexagonal shape */}
            {(!loading || showAddAnother) && imageBase64.length < 3 && (
              <div className={`relative ${imageBase64.length === 0 ? 'w-32 h-32' : 'w-24 h-24'}`}>
                <svg
                  className="w-full h-full cursor-pointer transition-all duration-200 hover:opacity-80"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  onClick={() => {
                    setImageMode('ai');
                    openVideoCamera();
                  }}
                >
                  <defs>
                    <clipPath id="hexClipPlus">
                      <path d="M 32 2 L 68 2 Q 70 2 71 3.5 L 97 48 Q 98 50 97 52 L 71 96.5 Q 70 98 68 98 L 32 98 Q 30 98 29 96.5 L 3 52 Q 2 50 3 48 L 29 3.5 Q 30 2 32 2 Z" />
                    </clipPath>
                  </defs>
                  {/* Background */}
                  <path
                    d="M 32 2 L 68 2 Q 70 2 71 3.5 L 97 48 Q 98 50 97 52 L 71 96.5 Q 70 98 68 98 L 32 98 Q 30 98 29 96.5 L 3 52 Q 2 50 3 48 L 29 3.5 Q 30 2 32 2 Z"
                    fill="rgba(255, 255, 255, 0.05)"
                    className="backdrop-blur-sm"
                  />
                  {/* Border */}
                  <path
                    d="M 32 2 L 68 2 Q 70 2 71 3.5 L 97 48 Q 98 50 97 52 L 71 96.5 Q 70 98 68 98 L 32 98 Q 30 98 29 96.5 L 3 52 Q 2 50 3 48 L 29 3.5 Q 30 2 32 2 Z"
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.3)"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                  {/* Plus icon */}
                  <line x1="50" y1="35" x2="50" y2="65" stroke="rgba(255, 255, 255, 0.6)" strokeWidth="3" strokeLinecap="round" />
                  <line x1="35" y1="50" x2="65" y2="50" stroke="rgba(255, 255, 255, 0.6)" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </div>
            )}
          </div>
        )}

        {/* Question mode indicator - only show when input is focused */}
        {currentlyInQuestionMode && isInputFocused && (
          <div className="w-full text-center mb-1">
            <span className="text-xs text-muted-foreground">
              {t('data_insights.question_mode_hint', 'Asking a question — I\'ll analyze your data. Remove "?" to log instead.')}
            </span>
          </div>
        )}

        <div className={`relative flex-1 w-full rounded-2xl ${isMobile ? 'mobile-input-wrapper' : ''}`}>
          {audioBase64 ? (
            // Show waveform when audio is recorded
            <div className={`flex items-center gap-2 px-4 py-3 rounded-2xl border-2 ${isMobile && showMobileEnhancedUI ? 'mobile-gradient-input active-input' : 'border-input bg-background'}`}>
              <div className="flex items-center gap-0.5 w-32 h-6">
                {[3, 8, 4, 9, 5, 7, 3, 10, 6, 4, 8, 5, 9, 4, 7, 3, 8, 6, 5, 9, 4, 7, 3, 6].map((height, i) => (
                  <div
                    key={i}
                    className="w-1 bg-primary/60 rounded-full transition-all"
                    style={{ height: `${height * 2}px` }}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  setAudioBase64(null);
                  setAudioContentType(null);
                }}
                className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          ) : (
            <Input
              ref={textInputRef}
              value={text}
              onChange={(e) => {
                setText(e.target.value.slice(0, 100));
              }}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              placeholder={imageBase64.length > 0 ? t('quick_entry.type_description') : (displayedText || t('quick_entry.type_something'))}
              maxLength={100}
              className={`text-sm rounded-2xl border-2 ai-glow-input overflow-hidden text-ellipsis transition-all duration-200 ${isMobile ? `mobile-gradient-input ${showMobileEnhancedUI ? 'active-input' : ''}` : ''} ${displayedText && !text && imageBase64.length === 0 ? 'typing-cursor' : ''}`}
              disabled={hasActiveScans && !showAddAnother}
              style={{
                paddingRight: isMobile ? (isInputFocused ? '8rem' : '10rem') : '8rem',
                ...(isMobile && showMobileEnhancedUI ? {
                  color: 'white',
                  caretColor: 'white',
                  WebkitTextFillColor: 'white'
                } : {})
              }}
            />
          )}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {/* Microphone button */}
            {audioRecorderAvailable && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={(hasActiveScans && !showAddAnother) || isRecording || isPremium === undefined}
                onClick={handleAudioRecording}
                onMouseDown={(e) => e.preventDefault()}
                className={`h-8 w-8 rounded-full hover:bg-primary/10 transition-all duration-300 ${isMobile && showMobileEnhancedUI ? 'hover:bg-white/20' : ''}`}
              >
                <span className="relative">
                  <Mic className={`w-4 h-4 transition-colors duration-300 ${isRecording ? 'text-red-500 animate-pulse' : (isMobile && showMobileEnhancedUI) ? 'text-white' : 'text-primary dark:text-white system:text-white'}`} />
                  {audioBase64 && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-accent rounded-full" />}
                </span>
              </Button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={(hasActiveScans && !showAddAnother) || isPremium === undefined}
              className="hidden"
              id="quick-entry-camera"
            />
            <div className="relative">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={(hasActiveScans && !showAddAnother) || isPremium === undefined}
                onClick={() => {
                  // If audio is present or no metrics enabled, skip mode picker and open video camera directly for AI mode
                  if (audioBase64 || hasNoMetrics) {
                    setImageMode('ai');
                    openVideoCamera();
                  } else {
                    setShowImageModePicker(!showImageModePicker);
                  }
                }}
                onMouseDown={(e) => e.preventDefault()}
                className={`h-11 w-11 rounded-full hover:bg-primary/10 transition-all duration-300 ${isMobile && showMobileEnhancedUI ? 'hover:bg-white/20' : ''}`}
              >
                <span className="relative">
                  <Camera className={`w-4 h-4 transition-colors duration-300 ${isMobile && showMobileEnhancedUI ? 'text-white' : 'text-primary dark:text-white system:text-white'}`} />
                  {imageBase64.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-accent rounded-full" />}
                </span>
              </Button>

              {/* Image Mode Picker - Click outside to close */}
              {showImageModePicker && (
                <>
                  {/* Backdrop overlay */}
                  <div
                    className={`fixed inset-0 z-40 touch-manipulation transition-opacity duration-200 ${isPickerFadingOut ? 'opacity-0' : 'opacity-100'}`}
                    onClick={closeImageModePicker}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      closeImageModePicker();
                    }}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  />
                  <div className={`absolute bottom-full right-0 mb-2 bg-popover border rounded-xl shadow-xl overflow-hidden z-50 min-w-[180px] p-1 transition-all duration-200 ${isPickerFadingOut ? 'opacity-0 scale-95' : 'opacity-100 scale-100 animate-in fade-in zoom-in-95'}`}>
                    {!hasNoMetrics && (
                    <button
                      type="button"
                      onClick={() => {
                        setImageMode('progress');
                        setShowImageModePicker(false);
                        setIsPickerFadingOut(false);
                        openProgressCamera();
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      className="w-full px-3 py-2.5 text-sm text-left hover:bg-accent hover:text-accent-foreground transition-colors rounded-lg flex items-center gap-3 group"
                    >
                      <div className="p-1.5 rounded-md bg-gray-500/10 text-gray-500 group-hover:bg-gray-500/20 transition-colors opacity-70">
                        <Camera className="w-4 h-4 stroke-[1.5]" />
                      </div>
                      <span className="font-medium">{t('quick_entry.camera.progress_pic')}</span>
                    </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setImageMode('ai');
                        setShowImageModePicker(false);
                        setIsPickerFadingOut(false);
                        openVideoCamera();
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      className="w-full px-3 py-2.5 text-sm text-left hover:bg-accent hover:text-accent-foreground transition-colors rounded-lg flex items-center gap-3 mt-1 group"
                    >
                      <div className="p-1.5 rounded-md bg-purple-500/10 text-purple-500 group-hover:bg-purple-500/20 transition-colors relative">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          {/* Corner brackets */}
                          <path d="M3 7V5C3 3.89543 3.89543 3 5 3H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          <path d="M17 3H19C20.1046 3 21 3.89543 21 5V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          <path d="M21 17V19C21 20.1046 20.1046 21 19 21H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          <path d="M7 21H5C3.89543 21 3 20.1046 3 19V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          {/* Apple */}
                          <path d="M12 8C10.5 8 9 9 9 11C9 13.5 10 15.5 12 17C14 15.5 15 13.5 15 11C15 9 13.5 8 12 8Z" fill="currentColor" />
                          <path d="M12 8V6.5C12 6.5 11.5 6 11 6C10.5 6 10 6.5 10 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                      <span className="font-medium">{t('quick_entry.camera.scan_with_ai')}</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Trash button - appears when there's text or attachments */}
            <div
              className="overflow-hidden"
              style={{
                width: (text.trim() || imageBase64.length > 0 || audioBase64) ? "2rem" : 0,
                marginLeft: (text.trim() || imageBase64.length > 0 || audioBase64) ? "0.25rem" : 0,
                transition: "width 300ms ease, margin 300ms ease",
                pointerEvents: (text.trim() || imageBase64.length > 0 || audioBase64) ? "auto" : "none",
              }}
            >
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={(hasActiveScans && !showAddAnother) || isPremium === undefined}
                onClick={handleClearAll}
                className={`h-8 w-8 rounded-full hover:bg-red-500/10 transition-all duration-300 ${isMobile && showMobileEnhancedUI ? 'hover:bg-white/20' : ''}`}
              >
                <Trash2 className={`w-4 h-4 transition-colors duration-300 ${isMobile && showMobileEnhancedUI ? 'text-white hover:text-red-400' : 'text-muted-foreground hover:text-red-500'}`} />
              </Button>
            </div>

            {/* Desktop generate button (inside input) - also shown on mobile when not in overlay mode */}
            {(!isMobile || (isMobile && hasContent && !showOverlayMode)) && (
              <div
                className="overflow-hidden"
                style={{
                  width: (text.trim().length >= 3 || imageBase64.length > 0 || audioBase64) ? "2rem" : 0,
                  marginLeft: (text.trim().length >= 3 || imageBase64.length > 0 || audioBase64) ? "0.5rem" : 0,
                  transition: "width 300ms ease, margin 300ms ease, opacity 300ms ease",
                  pointerEvents: (text.trim().length >= 3 || imageBase64.length > 0 || audioBase64) ? "auto" : "none",
                }}
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="submit"
                        size="icon"
                        disabled={(hasActiveScans && !showAddAnother) || isPremium === undefined}
                        aria-label={t('quick_entry.send')}
                        onMouseDown={(e) => e.preventDefault()}
                        className="h-8 w-8 min-w-[2rem] rounded-full bg-gradient-to-r from-green-400 via-emerald-500 to-green-400 hover:from-green-500 hover:via-emerald-600 hover:to-green-500 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                      >
                        {(hasActiveScans && !showAddAnother) ? (
                          <Loader2 className="w-8 h-8 animate-spin text-white" />
                        ) : (
                          <ArrowUp className="w-8 h-8 text-white" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('quick_entry.send')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </div >

        {/* Mobile Generate Button - Large circular button underneath */}
        {
          isMobile && showMobileEnhancedUI && (
            <div className="relative flex flex-col items-center justify-center mt-4 mobile-button-enter">
              {/* Show submit button only when not actively scanning OR when user wants to add another */}
              {(!hasActiveScans || showAddAnother) && (
                <Button
                  type="submit"
                  disabled={isPremium === undefined}
                  aria-label={imageMode === 'progress' ? 'Upload Progress Pic' : 'Generate with AI'}
                  onMouseDown={(e) => e.preventDefault()}
                  className={`relative ${imageMode === 'progress' ? 'h-12 px-6 rounded-full' : 'h-16 w-16 rounded-full'} green-gradient-button text-white shadow-2xl hover:shadow-3xl transition-all duration-300 overflow-visible`}
                >
                  {imageMode === 'progress' ? (
                    <span className="flex items-center gap-2 text-sm font-medium relative z-10">
                      {t('quick_entry.next')}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  ) : (
                    <ArrowUp
                      className="w-28 h-28 text-white relative z-10 stroke-[2.5]"
                      viewBox="3 3 18 18"
                    />
                  )}
                </Button>
              )}

              {/* Active scans indicator - shows when scans are processing */}
              {hasActiveScans && !showAddAnother && (
                <div className="flex flex-col items-center mt-6 gap-4">
                  {/* Minimized loading indicator */}
                  <div className="flex items-center gap-3">
                    <MiniGridLoader className="w-5 h-5 text-white/70" />
                    <span className="text-sm text-white/70">
                      {activeScansCount === 1 
                        ? (t('quick_entry.analyzing') || 'Analyzing...')
                        : `${activeScansCount} items analyzing...`
                      }
                    </span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-48 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-blue-400 transition-none"
                      style={{ width: `${scanProgress}%` }}
                    />
                  </div>

                  {/* SSE Progress step - show only when single item is analyzing */}
                  {activeScansCount === 1 && progressSteps.length > 0 && (
                    <span className="text-xs text-white/50 animate-pulse">
                      {progressSteps[progressSteps.length - 1]?.message || ''}
                    </span>
                  )}
                  
                  {/* Queue indicator if results are waiting */}
                  {scanResultsQueue.length > 0 && (
                    <span className="text-xs text-white/50">
                      {scanResultsQueue.length} result{scanResultsQueue.length > 1 ? 's' : ''} ready
                    </span>
                  )}
                  
                  {/* Log another item button */}
                  <Button
                    type="button"
                    onClick={() => {
                      // Clear input when user wants to add another item
                      setText("");
                      setImageBase64([]);
                      setAudioBase64(null);
                      setAudioContentType(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                      setShowAddAnother(true);
                    }}
                    className="mt-2 px-6 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full text-white border border-white/30 transition-all"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('quick_entry.log_another') || 'Log another item'}
                  </Button>
                  
                  {/* Cancel all scans button */}
                  <button
                    type="button"
                    onClick={() => {
                      // Abort all in-flight scan requests so they don't open dialogs after cancel
                      scanAbortControllersRef.current.forEach(c => c.abort());
                      scanAbortControllersRef.current = [];
                      setActiveScansCount(0);
                      setScanResultsQueue([]);
                      setLoading(false);
                      setShowAddAnother(false);
                      setProgressSteps([]);
                      setScanProgress(0);
                      scanProgressRef.current = 0;
                      if (scanProgressRafRef.current) {
                        cancelAnimationFrame(scanProgressRafRef.current);
                        scanProgressRafRef.current = null;
                      }
                      setText("");
                      setImageBase64([]);
                      setAudioBase64(null);
                      setAudioContentType(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                    className="mt-1 text-xs text-white/40 hover:text-white/70 transition-colors"
                  >
                    {t('quick_metrics.cancel') || 'Cancel'}
                  </button>
                </div>
              )}
              
              {/* Show cycling phrases only when user is viewing the loading (not adding another) */}
              {hasActiveScans && showAddAnother && (
                <div className="flex items-center gap-2 mt-4 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full">
                  <MiniGridLoader className="w-4 h-4 text-white/70" />
                  <span className="text-xs text-white/70">
                    {activeScansCount} item{activeScansCount > 1 ? 's' : ''} analyzing
                    {scanResultsQueue.length > 0 ? `, ${scanResultsQueue.length} ready` : ''}
                  </span>
                </div>
              )}
            </div>
          )
        }
      </form >

      <QuickEntryConfirmDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        suggestedValues={suggestedValues}
        userId={userId}
        selectedDate={selectedDate}
        allMetrics={allEnabledMetrics}
        metricPreferences={metricPrefs}
        customMetrics={customMetrics || []}
        originalInput={text}
        freeFixAvailable={freeFixAvailable}
        onEmojiExplosion={(emojis) => {
          console.log("[QuickEntryInput] Received emoji explosion request:", emojis);
          setExplosionEmojis(emojis);
          setShowEmojiExplosion(true);
        }}
        onTutorialTrigger={(metricInfo) => {
          // Store the tutorial trigger callback to fire after emoji animation
          if (onTutorialTrigger) {
            setPendingTutorialTrigger(() => () => onTutorialTrigger(metricInfo));
          }
        }}
        onFixInChat={() => {
          // Close dialog without clearing suggestedValues (chat needs them!)
          setDialogOpen(false);
          setFixInChatOpen(true);
        }}
      />

      {/* Fix in Chat Dialog */}
      <FixInChatDialog
        open={fixInChatOpen}
        onOpenChange={(open) => {
          setFixInChatOpen(open);
          // Clear suggestedValues and freeFixAvailable when chat dialog closes
          if (!open) {
            setText("");
            setImageBase64([]);
            setAudioBase64(null);
            setAudioContentType(null);
            setSuggestedValues(null);
            setFreeFixAvailable(false);
            
            // Process next item in the scan results queue
            setScanResultsQueue(prev => {
              if (prev.length > 0) {
                const [nextResult, ...rest] = prev;
                // Use setTimeout to avoid state update conflicts
                setTimeout(() => {
                  setSuggestedValues(nextResult.suggestedValues);
                  setFreeFixAvailable(nextResult.freeFixAvailable);
                  setDialogOpen(true);
                }, 300); // Small delay for smooth transition between dialogs
                return rest;
              } else {
                // Queue is empty - check if we should trigger the tutorial
                // Use setTimeout to ensure state has settled
                setTimeout(() => {
                  if (pendingTutorialTriggerRef.current && activeScansCountRef.current === 0) {
                    console.log("[QuickEntryInput] All dialogs done (from FixInChatDialog) - triggering tutorial");
                    pendingTutorialTriggerRef.current();
                    setPendingTutorialTrigger(null);
                  }
                }, 400); // Slightly longer delay to ensure emoji explosion has completed
              }
              return prev;
            });
          }
        }}
        userId={userId}
        selectedDate={selectedDate}
        originalInput={text}
        suggestedValues={suggestedValues}
        customMetrics={customMetrics || []}
        userCredits={creditsData?.credits}
        isPremium={isPremium}
        freeFixAvailable={freeFixAvailable}
        onFreeFixUsed={() => setFreeFixAvailable(false)}
        onLogValues={async (values) => {
          // Log the values from chat directly to the database
          const entryDate = format(selectedDate, "yyyy-MM-dd");
          const metricDefinitions = getAllMetrics();
          
          try {
            // Prepare values for saving
            const valuesToSave: Record<string, number | boolean> = {};
            const loggedEmojis: string[] = [];
            
            for (const [key, value] of Object.entries(values)) {
              if (value !== undefined && value !== null) {
                valuesToSave[key] = typeof value === 'boolean' ? (value ? 1 : 0) : Number(value);
                
                // Collect emojis for explosion
                if (key.startsWith('custom_')) {
                  const customMetric = (customMetrics || []).find(cm => cm.metric_key === key);
                  if (customMetric?.metric_emoji) {
                    loggedEmojis.push(customMetric.metric_emoji);
                  }
                } else {
                  const metricDef = metricDefinitions[key];
                  if (metricDef?.emoji) {
                    loggedEmojis.push(metricDef.emoji);
                  }
                }
              }
            }

            // Upsert daily entry
            const { error } = await supabase
              .from("daily_entries")
              .upsert({
                user_id: userId,
                entry_date: entryDate,
                ...valuesToSave,
              }, {
                onConflict: "user_id,entry_date",
              });

            if (error) {
              console.error("[FixInChatDialog] Error saving values:", error);
              toast.error(t('components.quick_entry_confirm.toasts.save_error'));
              return;
            }

            toast.success(t('components.quick_entry_confirm.toasts.values_logged'));
            
            // Close the chat dialog
            setFixInChatOpen(false);
            
            // Trigger emoji explosion
            if (loggedEmojis.length > 0) {
              setExplosionEmojis(loggedEmojis);
              setShowEmojiExplosion(true);
            }

            // Refresh data - invalidate all relevant queries
            queryClient.invalidateQueries({ queryKey: ["daily-entry", userId, entryDate] });
            queryClient.invalidateQueries({ queryKey: ["daily-entry", userId] });
            queryClient.invalidateQueries({ queryKey: ["daily-entries", userId] });
            queryClient.invalidateQueries({ queryKey: ["daily-entries"] });
            queryClient.invalidateQueries({ queryKey: ["user-credits", userId] });
          } catch (err) {
            console.error("[FixInChatDialog] Error saving values:", err);
            toast.error(t('components.quick_entry_confirm.toasts.save_error'));
          }
        }}
        onEmojiExplosion={(emojis) => {
          setExplosionEmojis(emojis);
          setShowEmojiExplosion(true);
        }}
      />

      {/* Data Insights Dialog - for analytical questions about user data */}
      <DataInsightsDialog
        open={dataInsightsOpen}
        onOpenChange={(open) => {
          setDataInsightsOpen(open);
          if (!open) {
            setInitialAnalyticalQuestion("");
          }
        }}
        userId={userId}
        initialQuestion={initialAnalyticalQuestion}
        userCredits={creditsData?.credits}
      />

      {/* Emoji Explosion Animation */}
      {
        showEmojiExplosion && explosionEmojis.length > 0 && (
          <EmojiExplosion
            emojis={explosionEmojis}
            onComplete={() => {
              console.log("[QuickEntryInput] Explosion complete");
              setShowEmojiExplosion(false);
              setExplosionEmojis([]);

              // Only trigger tutorial if all dialogs are done processing
              // Check refs since state values are stale in this callback
              const hasMoreDialogs = scanResultsQueueRef.current.length > 0;
              const hasActiveScans = activeScansCountRef.current > 0;
              
              if (pendingTutorialTrigger) {
                if (hasMoreDialogs || hasActiveScans) {
                  // More dialogs pending - keep the trigger for later
                  console.log("[QuickEntryInput] Deferring tutorial trigger - more dialogs pending", {
                    queueLength: scanResultsQueueRef.current.length,
                    activeScans: activeScansCountRef.current
                  });
                } else {
                  // All dialogs done - fire the tutorial now
                  console.log("[QuickEntryInput] All dialogs done - triggering tutorial");
                  pendingTutorialTrigger();
                  setPendingTutorialTrigger(null);
                }
              }
            }}
          />
        )
      }

      {/* Report Issue Button - shown when dialogs are open, portaled above dialog overlay */}
      {(dialogOpen || fixInChatOpen) && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, delay: 0.5 }}
            style={{ position: 'fixed', bottom: '2rem', left: 0, right: 0, zIndex: 200, display: 'flex', justifyContent: 'flex-end', paddingRight: '1.5rem', pointerEvents: 'none' }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setDialogOpen(false);
                setFixInChatOpen(false);
                navigate('/report-bug');
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="text-[11px] text-muted-foreground/50 hover:text-muted-foreground/80 transition-colors bg-transparent border-none cursor-pointer"
              style={{ pointerEvents: 'auto' }}
            >
              Report issue
            </button>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Camera Tips Dialog */}
      <CameraTipsDialog
        open={showCameraTips}
        onOpenChange={setShowCameraTips}
        initialFingerSize={(fingerSize as "small" | "medium" | "large") || "medium"}
        onProceed={(selectedFingerSize) => {
          // Open camera immediately to satisfy browser security policies
          // This must happen synchronously with the user event
          fileInputRef.current?.click();

          // Don't close dialog here - let handleImageUpload close it when image is received
          // setShowCameraTips(false); // REMOVED - dialog closes when image is returned

          // Save finger size to database in background
          saveFingerSize(selectedFingerSize);
        }}
      />

    </>
  );
};
